import { useEffect, useState, useMemo, useRef } from 'react';
import { AlreadyPostedButton } from '../components/AlreadyPostedButton';
import { DeliveryBadge, DELIVERY_STYLE, stageDeliveryExtra, deliveryToast } from '../components/DeliveryDialog';
import { PostDayButton } from '../components/PostDayButton';
import ProjectPostageDialog from '../components/ProjectPostageDialog';
import PostOrderDialog from '../components/PostOrderDialog';
import TransferPosterDialog from '../components/TransferPosterDialog';
import { PublicationCheckDialog, CoversDialog, PubCheckBadge } from '../components/ProjectPublicationTools';
import { createPortal } from 'react-dom';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import { Avatar } from '../components/Logo';
import { STAGES, STAGE_ORDER, PRIORITIES, formatRelative } from '../constants/users';
import { DetailSidebar } from './TasksPage';
import ProjectWizardV3 from '../components/ProjectWizardV3';
import ReplanDialog from '../components/ReplanDialog';
import AddVideoDialog from '../components/AddVideoDialog';
import AIRearrangeDialog from '../components/AIRearrangeDialog';
import {
  Plus, X, Trash2, Loader2, ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight, ChevronDown,
  ProjectIcon, PROJECT_ICONS, Camera, Video, Palette, Briefcase, Layers, Mic, Music2, Zap, Flame, Star,
  Check, Clock, LinkIcon, Lightbulb, FileText, Folder, Inbox, Sparkles, Pencil, ExternalLink,
  AlertTriangle, ArrowRight, Calendar, GripVertical,
} from '../components/icons';

const ICON_CHOICES = Object.keys(PROJECT_ICONS);
const COLOR_CHOICES = ['#5BBEFC', '#2EA3F2', '#A78BFA', '#F472B6', '#FBBF24', '#34D399', '#F5A623', '#FF6B6B'];

export default function ProjectsPage() {
  const { isFounder, user: me } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [collapsedFolders, setCollapsedFolders] = useState(() => {
    try { return JSON.parse(localStorage.getItem('asa_collapsed_folders') || '[]'); }
    catch { return []; }
  });
  const [loading, setLoading] = useState(true);
  const [showFolderStep, setShowFolderStep] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showChoice, setShowChoice] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showFramework, setShowFramework] = useState(null); // framework id
  const [showFromDoc, setShowFromDoc] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [showRequestsInbox, setShowRequestsInbox] = useState(false);
  const [openProject, setOpenProject] = useState(null);
  const [phase2PrefillId, setPhase2PrefillId] = useState(null); // Iter34: pre-select project for Phase 2
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter47l — Client folder picker before wizard (user's request: choisir le dossier client d'abord)
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [prefilledClient, setPrefilledClient] = useState('');

  const reload = async () => {
    const [p, u, f, r] = await Promise.all([
      api.get('/projects'),
      api.get('/users'),
      api.get('/folders'),
      api.get('/project-requests').catch(() => ({ data: [] })),
    ]);
    setProjects(p.data); setUsers(u.data); setFolders(f.data);
    setRequests(r.data || []);
    setLoading(false);
    // Auto-open project if coming from Tasks page
    const pendingId = localStorage.getItem('asa_open_project');
    if (pendingId) {
      localStorage.removeItem('asa_open_project');
      const target = p.data.find((x) => x.id === pendingId);
      if (target) setOpenProject(target);
    }
  };
  useEffect(() => { reload(); }, []);

  // === Iter34 : open wizard in Phase 2 mode if ?phase2={pid} is in the URL
  useEffect(() => {
    const pid = searchParams.get('phase2');
    if (pid && isFounder && !showCreate) {
      setPhase2PrefillId(pid);
      setSelectedFolder('internal');
      setShowCreate(true);
      // Clean URL after opening
      const next = new URLSearchParams(searchParams);
      next.delete('phase2');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, isFounder, showCreate, setSearchParams]);

  // Iter42c — view mode & sort mode persisted in localStorage
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('asa_projects_view') || 'cards'; }
    catch { return 'cards'; }
  });
  const [sortMode, setSortMode] = useState(() => {
    try { return localStorage.getItem('asa_projects_sort') || 'folder'; }
    catch { return 'folder'; }
  });
  const changeView = (v) => { setViewMode(v); try { localStorage.setItem('asa_projects_view', v); } catch (_) { /* noop */ } };
  const changeSort = (s) => { setSortMode(s); try { localStorage.setItem('asa_projects_sort', s); } catch (_) { /* noop */ } };

  const toggleFolder = (fid) => {
    setCollapsedFolders((prev) => {
      const next = prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid];
      try { localStorage.setItem('asa_collapsed_folders', JSON.stringify(next)); } catch (_) { /* noop */ }
      return next;
    });
  };

  // Iter47e — Group projects by CLIENT (was: by folder type like YouTube/X/etc which was useless).
  // Each unique client becomes a "folder". Projects without a client go into "Sans client".
  const projectsByFolder = useMemo(() => {
    const grouped = new Map();
    projects
      .filter((p) => !p.archived && (p.progress?.ratio ?? 0) < 1)
      .forEach((p) => {
        const key = (p.client || '').trim() || '__no_client__';
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(p);
      });
    return grouped;
  }, [projects]);

  // Iter47e — Build a synthetic folders array from the client keys (used for headers / rendering)
  const clientFolders = useMemo(() => {
    const keys = Array.from(projectsByFolder.keys());
    // sort alphabetically, but keep "Sans client" bucket at the end
    const sorted = keys.sort((a, b) => {
      if (a === '__no_client__') return 1;
      if (b === '__no_client__') return -1;
      return a.localeCompare(b, 'fr');
    });
    return sorted.map((k) => ({
      id: k,
      label: k === '__no_client__' ? 'Sans client' : k,
      color: k === '__no_client__' ? '#8A9DB8' : '#5BBEFC',
    }));
  }, [projectsByFolder]);

  // Iter43c — Filter out fully-completed projects (progress.ratio >= 1)
  // and archived projects across ALL views. They stay accessible in /archives.
  const visibleProjects = useMemo(() =>
    projects.filter((p) => !p.archived && (p.progress?.ratio ?? 0) < 1),
    [projects]
  );

  // Iter42c — build a flat sorted list for the non-folder views
  const sortedProjects = useMemo(() => {
    const arr = [...visibleProjects];
    if (sortMode === 'deadline_near') {
      arr.sort((a, b) => {
        const da = a.end_date || '9999';
        const db = b.end_date || '9999';
        return da.localeCompare(db);
      });
    } else if (sortMode === 'deadline_far') {
      arr.sort((a, b) => {
        const da = a.end_date || '';
        const db = b.end_date || '';
        return db.localeCompare(da);
      });
    } else if (sortMode === 'recent') {
      arr.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
    return arr;
  }, [visibleProjects, sortMode]);

  const pendingRequests = useMemo(
    () => requests.filter((r) => r.status === 'pending'),
    [requests],
  );

  if (openProject) return (
    <ProjectDetailView
      project={openProject}
      users={users}
      isFounder={isFounder || (openProject.supervisors || []).includes(me?.id)}
      onBack={() => { setOpenProject(null); reload(); }}
      onDelete={() => { setOpenProject(null); reload(); }}
    />
  );

  return (
    <div className="page-fade">
      <PageHeader
        title="Projets"
        subtitle={isFounder ? "Tes campagnes et productions en cours" : "Tes projets et tes demandes en cours"}
        count={projects.length || null}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            {isFounder && <AlreadyPostedButton onDone={reload} />}
            {isFounder && pendingRequests.length > 0 && (
              <button
                data-testid="project-requests-inbox"
                onClick={() => setShowRequestsInbox(true)}
                className="asa-btn"
                style={{
                  background: 'rgba(255,107,107,0.18)',
                  border: '1.5px solid rgba(255,107,107,0.4)',
                  color: '#FF6B6B',
                }}
                title={`${pendingRequests.length} demande${pendingRequests.length > 1 ? 's' : ''} en attente`}
              >
                <Inbox size={14} /> Demandes
                <span style={{
                  background: '#FF6B6B', color: '#fff',
                  padding: '1px 7px', borderRadius: 999,
                  fontFamily: 'Sora', fontWeight: 800, fontSize: '0.62rem',
                  marginLeft: 4,
                }}>{pendingRequests.length}</span>
              </button>
            )}
            {isFounder ? (
              <button
                data-testid="project-new-btn"
                onClick={() => setShowClientPicker(true)}
                className="asa-btn"
              >
                <Plus size={15} /> Nouveau
              </button>
            ) : (
              <button
                data-testid="project-request-btn"
                onClick={() => setShowRequest(true)}
                className="asa-btn"
              >
                <Plus size={15} /> Demander
              </button>
            )}
          </div>
        }
      />

      <div style={{ padding: '0 22px 100px' }}>
        {/* Iter42c — Sort tabs + view toggle */}
        {!loading && projects.length > 0 && (
          <div
            data-testid="projects-toolbar"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              marginBottom: 18, paddingBottom: 12,
              borderBottom: '1px solid var(--bd)',
            }}
          >
            <div style={{ display: 'flex', gap: 4, background: 'var(--sf2)', borderRadius: 999, padding: 3 }}>
              {[
                { id: 'folder',        label: 'Par client' },
                { id: 'deadline_near', label: 'Deadline proche' },
                { id: 'deadline_far',  label: 'Deadline loin' },
                { id: 'recent',        label: 'Récents' },
              ].map((s) => (
                <button
                  key={s.id}
                  data-testid={`projects-sort-${s.id}`}
                  onClick={() => changeSort(s.id)}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: sortMode === s.id ? 'var(--p)' : 'transparent',
                    color: sortMode === s.id ? '#0a1a33' : 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    border: 'none', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >{s.label}</button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 4, background: 'var(--sf2)', borderRadius: 999, padding: 3 }}>
              {[
                { id: 'cards',   label: 'Cards' },
                { id: 'compact', label: 'Compact' },
              ].map((v) => (
                <button
                  key={v.id}
                  data-testid={`projects-view-${v.id}`}
                  onClick={() => changeView(v.id)}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: viewMode === v.id ? '#5BBEFC' : 'transparent',
                    color: viewMode === v.id ? '#0a1a33' : 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                    border: 'none', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >{v.label}</button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="shimmer" style={{ height: 240, borderRadius: 16 }} />)}
          </div>
        ) : projects.length === 0 ? (
          <EmptyProjects onCreate={() => setShowCreate(true)} />
        ) : sortMode !== 'folder' ? (
          // Iter42c — Flat sorted view (deadline near / far / recent)
          viewMode === 'compact' ? (
            <ProjectsCompactList projects={sortedProjects} isFounder={isFounder} onOpen={setOpenProject} />
          ) : (
            <div className="stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
              {sortedProjects.map((p) => (
                <ProjectCard key={p.id} project={p} isFounder={isFounder} onClick={() => setOpenProject(p)} />
              ))}
            </div>
          )
        ) : (
          <div className="stagger" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
            {clientFolders.map((f) => {
              const ps = projectsByFolder.get(f.id) || [];
              if (ps.length === 0) return null;
              const collapsed = collapsedFolders.includes(f.id);
              return (
                <div key={f.id} data-testid={`folder-section-${f.id}`}>
                  <button
                    onClick={() => toggleFolder(f.id)}
                    data-testid={`folder-toggle-${f.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '8px 4px 12px', marginBottom: 4,
                      background: 'transparent', border: 'none',
                      borderBottom: `1.5px solid ${f.color}33`,
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <ChevronDown
                      size={14}
                      color={f.color}
                      style={{
                        transition: 'transform 0.2s var(--ease-out)',
                        transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
                      }}
                    />
                    <div style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: `${f.color}1A`,
                      border: `1.5px solid ${f.color}55`,
                      display: 'grid', placeItems: 'center', flexShrink: 0,
                    }}>
                      <Folder size={13} color={f.color} strokeWidth={1.8} />
                    </div>
                    <span style={{
                      fontFamily: 'Sora', fontWeight: 800, fontSize: '0.96rem',
                      color: 'var(--t1)', letterSpacing: '-0.01em',
                    }}>{f.label}</span>
                    <span style={{
                      fontFamily: 'Sora', fontWeight: 700, fontSize: '0.64rem',
                      padding: '2px 8px', borderRadius: 999,
                      background: `${f.color}1F`, color: f.color,
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>{ps.length} projet{ps.length > 1 ? 's' : ''}</span>
                  </button>
                  {!collapsed && (
                    viewMode === 'compact' ? (
                      <div style={{ marginTop: 8 }}>
                        <ProjectsCompactList projects={ps} isFounder={isFounder} onOpen={setOpenProject} />
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14, marginTop: 14 }}>
                        {ps.map((p) => <ProjectCard key={p.id} project={p} isFounder={isFounder} onClick={() => setOpenProject(p)} />)}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showFolderStep && (
        <FolderStepDialog
          folders={folders}
          onClose={() => setShowFolderStep(false)}
          onCreated={(newFolder) => { setFolders((prev) => [...prev, newFolder]); }}
          onPick={(fid) => {
            setSelectedFolder(fid);
            setShowFolderStep(false);
            // Iter31: go straight to the unified wizard (manual/doc selection inside)
            setShowCreate(true);
          }}
        />
      )}

      {showCreate && (
        <ProjectWizardV3
          users={users}
          folderId={selectedFolder}
          prefillPhase2ProjectId={phase2PrefillId}
          prefilledClient={prefilledClient}
          onClose={() => { setShowCreate(false); setSelectedFolder(null); setPhase2PrefillId(null); setPrefilledClient(''); }}
          onSaved={(project) => {
            setShowCreate(false); setSelectedFolder(null); setPhase2PrefillId(null); setPrefilledClient('');
            reload();
            if (project) setOpenProject(project);
          }}
        />
      )}

      {showClientPicker && (
        <ClientFolderPicker
          projects={projects}
          onClose={() => setShowClientPicker(false)}
          onPick={(clientName) => {
            setPrefilledClient(clientName);
            setShowClientPicker(false);
            setShowCreate(true);
          }}
        />
      )}

      {showRequest && (
        <ProjectRequestDialog
          onClose={() => setShowRequest(false)}
          onSaved={() => { setShowRequest(false); reload(); toast.success('Demande envoyée à Rayan et Hugo'); }}
        />
      )}

      {showRequestsInbox && (
        <ProjectRequestsInbox
          requests={requests}
          users={users}
          onClose={() => setShowRequestsInbox(false)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project card (grid)
// ---------------------------------------------------------------------------
// ============================================================================
// ProjectsCompactList (Iter42c) — Google-Drive-style dense list view.
// One line per project : icon · name · client · % · deadline · created date.
// ============================================================================
function ProjectsCompactList({ projects, isFounder, onOpen }) {
  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch { return iso; }
  };
  return (
    <div data-testid="projects-compact-list" style={{
      display: 'flex', flexDirection: 'column', gap: 3,
      border: '1px solid var(--bd)', borderRadius: 10, overflow: 'hidden',
    }}>
      {/* Header row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '28px minmax(180px, 3fr) minmax(100px, 1.5fr) 60px 90px 90px 90px',
        gap: 10, padding: '8px 14px',
        background: 'var(--sf2)',
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
        color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em',
        borderBottom: '1px solid var(--bd)',
      }}>
        <span />
        <span>Projet</span>
        <span>Client</span>
        <span style={{ textAlign: 'right' }}>%</span>
        <span>Deadline</span>
        <span>Créé</span>
        <span style={{ textAlign: 'right' }}>Tâches</span>
      </div>
      {projects.map((p) => {
        const { total = 0, done = 0, ratio = 0 } = p.progress || {};
        const pct = Math.round(ratio * 100);
        const dl = p.end_date;
        const isP1Pending = p.phase_stage === 'p1_only';
        return (
          <button
            key={p.id}
            data-testid={`project-compact-${p.id}`}
            onClick={() => onOpen(p)}
            style={{
              display: 'grid',
              gridTemplateColumns: '28px minmax(180px, 3fr) minmax(100px, 1.5fr) 60px 90px 90px 90px',
              gap: 10, padding: '10px 14px',
              alignItems: 'center', textAlign: 'left',
              background: 'transparent', border: 'none',
              borderBottom: '1px solid var(--bd)',
              cursor: 'pointer', width: '100%',
              transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{
              width: 22, height: 22, borderRadius: 6,
              background: `${p.color}1A`, border: `1.2px solid ${p.color}55`,
              display: 'grid', placeItems: 'center',
            }}>
              <ProjectIcon name={p.icon} size={12} color={p.color} />
            </span>
            <span style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {p.name}
              {isP1Pending && (
                <span style={{
                  fontSize: 9, padding: '1px 6px', borderRadius: 999,
                  background: 'rgba(251,191,36,0.16)', color: '#FBBF24',
                  fontFamily: 'Sora', fontWeight: 700, letterSpacing: '0.04em',
                }}>P1</span>
              )}
            </span>
            <span style={{
              fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{p.client || '—'}</span>
            <span style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: p.color,
              textAlign: 'right',
            }}>{pct}%</span>
            <span style={{
              fontFamily: 'Lexend', fontSize: 11, color: dl ? 'var(--t2)' : 'var(--t3)',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {dl && <Calendar size={10} />}
              {fmt(dl)}
            </span>
            <span style={{
              fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)',
            }}>{fmt(p.created_at)}</span>
            <span style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: 'var(--t3)',
              textAlign: 'right',
            }}>{done}/{total}</span>
          </button>
        );
      })}
    </div>
  );
}

function ProjectCard({ project, onClick, isFounder }) {
  const { total = 0, done = 0, ratio = 0 } = project.progress || {};
  const pct = Math.round(ratio * 100);
  const navigate = useNavigate();
  // Iter38 — Phase 2 CTA on card (founder only, phase_stage=p1_only, all dérush done)
  const showPhase2Cta = isFounder
    && project.phase_stage === 'p1_only'
    && project.phase1_done
    && !project.archived;
  const isP1Pending = !showPhase2Cta && project.phase_stage === 'p1_only';
  return (
    <button
      onClick={onClick}
      data-testid={`project-card-${project.id}`}
      className="asa-card hover-grow"
      style={{
        position: 'relative', overflow: 'hidden',
        padding: 16, textAlign: 'left',
        display: 'flex', flexDirection: 'column',
        minHeight: 130, cursor: 'pointer',
        gap: 10,
      }}
    >
      {/* Iter43 — magazine-style gradient cover on top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 44,
        background: `linear-gradient(135deg, ${project.color}22, ${project.color}05)`,
        borderBottom: `1px solid ${project.color}22`,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, ${project.color}, ${project.color}55)`,
      }} />

      {/* Iter38 — Phase 1 / Phase 2 status badge */}
      {(isP1Pending || showPhase2Cta) && (
        <div data-testid={`project-phase-badge-${project.id}`} style={{
          position: 'absolute', top: 8, right: 8,
          padding: '2px 7px', borderRadius: 999,
          background: showPhase2Cta ? 'rgba(167,139,250,0.20)' : 'rgba(245,158,11,0.20)',
          border: `1.5px solid ${showPhase2Cta ? '#A78BFA' : '#F59E0B'}`,
          color: showPhase2Cta ? '#A78BFA' : '#F59E0B',
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.54rem',
          letterSpacing: 0.4,
          zIndex: 1,
        }}>
          {showPhase2Cta ? 'P1 · P2' : 'P1'}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, position: 'relative', zIndex: 1 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: `${project.color}22`,
          border: `1.5px solid ${project.color}66`,
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <ProjectIcon name={project.icon} size={15} color={project.color} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="asa-heading" style={{
            fontSize: '0.92rem', lineHeight: 1.2, color: 'var(--t1)',
            letterSpacing: '-0.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {project.name}
          </div>
          {project.client && (
            <div style={{
              fontFamily: 'Lexend', fontSize: '0.68rem', color: 'var(--t3)',
              marginTop: 2, fontWeight: 500,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {project.client}
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1 }} />

      {/* Progress bar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '0.92rem', color: project.color, lineHeight: 1 }}>
            {pct}<span style={{ fontSize: '0.6rem', opacity: 0.7 }}>%</span>
          </span>
          <span style={{ fontFamily: 'Lexend', fontSize: '0.62rem', color: 'var(--t3)' }}>
            {done}/{total}
          </span>
        </div>
        <div style={{
          height: 4, borderRadius: 2, overflow: 'hidden',
          background: 'var(--sf2)',
        }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: `linear-gradient(90deg, ${project.color}, ${project.color}AA)`,
            transition: 'width 0.4s var(--ease-out)',
          }} />
        </div>
      </div>

      {project.tags && project.tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {project.tags.slice(0, 4).map((t) => (
            <span key={t} style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: '0.74rem',
              padding: '5px 10px', borderRadius: 8,
              background: 'var(--sf2)', color: 'var(--t2)',
            }}>#{t}</span>
          ))}
        </div>
      )}

      {/* Iter38 — Phase 2 CTA bar */}
      {showPhase2Cta && (
        <div
          data-testid={`project-phase2-cta-${project.id}`}
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); navigate(`/projects?phase2=${project.id}`); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate(`/projects?phase2=${project.id}`); } }}
          style={{
            padding: '12px 16px', borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(167,139,250,0.22), rgba(91,190,252,0.16))',
            border: '1.5px solid rgba(167,139,250,0.50)',
            display: 'flex', alignItems: 'center', gap: 10,
            fontFamily: 'Sora', fontWeight: 700, fontSize: '0.88rem',
            color: '#A78BFA', cursor: 'pointer',
          }}
        >
          <Sparkles size={15} />
          <span style={{ flex: 1 }}>Phase 1 terminée — Lancer la Phase 2</span>
          <ArrowRight size={15} />
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Project detail (flowchart of tasks)
// ---------------------------------------------------------------------------
// Founder ou superviseur du projet ouvert (pas de pop-up Drive/Frame pour eux).
const CAN_MANAGE = { current: false };

function ProjectDetailView({ project: initialProject, users, isFounder, onBack, onDelete }) {
  CAN_MANAGE.current = !!isFounder;
  // Iter 62do — Deep-link : si asa_open_task_row en localStorage AU MOUNT,
  // on cache le projet derrière un overlay noir jusqu'à l'ouverture du modal.
  const [pendingDeepLink, setPendingDeepLink] = useState(() => {
    try { return !!localStorage.getItem('asa_open_task_row'); } catch { return false; }
  });
  const [project, setProject] = useState(initialProject);
  const [tasks, setTasks] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showAIRearrange, setShowAIRearrange] = useState(false); // Iter 62et — Rangement par IA (remplace IA doc)
  const [groupByFormat, setGroupByFormat] = useState(() => {
    try { return localStorage.getItem('asa_group_by_format') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('asa_group_by_format', groupByFormat ? '1' : '0'); } catch {}
  }, [groupByFormat]);
  // Iter 62fl — Toggle vue Liste / Kanban (persist localStorage)
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('asa_project_view_mode') || 'list'; } catch { return 'list'; }
  });
  useEffect(() => {
    try { localStorage.setItem('asa_project_view_mode', viewMode); } catch {}
  }, [viewMode]);
  const [showReplan, setShowReplan] = useState(false);
  const [showPostage, setShowPostage] = useState(false);
  const [showPostOrder, setShowPostOrder] = useState(false);
  const [showTransferPoster, setShowTransferPoster] = useState(false);
  const [showPubCheck, setShowPubCheck] = useState(false);
  const [titleFilter, setTitleFilter] = useState(false);
  const [pubCheckRows, setPubCheckRows] = useState(null);
  const [showCovers, setShowCovers] = useState(false);
  // Iter 62fo — Calendrier des posts (toutes les vidéos du projet par date)
  const [showCalendar, setShowCalendar] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showInspirationModal, setShowInspirationModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  // Iter40 — Sync `initialProject` prop (parent) → local `project` state.
  // Without this, a parent update of openProject after re-open would not reach the child,
  // and the local `project` could go stale after rename/edit cycles.
  useEffect(() => {
    if (initialProject && initialProject.id === project.id) {
      setProject((prev) => ({ ...prev, ...initialProject }));
    }
  }, [initialProject, project.id]);

  const reload = async () => {
    try {
      const [t, p, c] = await Promise.all([
        api.get(`/tasks?project_id=${project.id}`),
        api.get('/projects'),
        api.get(`/concepts?project_id=${project.id}`).catch(() => ({ data: [] })),
      ]);
      setTasks(t.data);
      setConcepts(c.data || []);
      // Iter40 — keep current project state even if listing endpoint dropped it
      // (auto-archive at 100% removes finished projects from /projects).
      const fresh = p.data.find((x) => x.id === project.id);
      if (fresh) {
        setProject(fresh);
      } else {
        // Project may have been auto-archived → fetch it directly so UI stays alive
        try {
          const single = await api.get(`/projects/${project.id}`);
          if (single?.data) setProject((prev) => ({ ...prev, ...single.data }));
        } catch (_) { /* keep current */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de rafraîchissement');
    } finally {
      setLoading(false);
    }
  };

  // Iter41 (post-deploy) — Optimistic task update.
  // If the backend returns the updated task (e.g. POST /tasks/:id/phase-step,
  // PATCH /tasks/:id), we patch the local `tasks` array immediately so the UI
  // reflects the change WITHOUT any reload. We fall back to a full reload only
  // when no `updated` payload is given (legacy callers).
  const handleTaskChange = (updated) => {
    if (updated && updated.id) {
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    } else {
      reload();
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [project.id]);

  // Iter 62dm/dn/do — Deep-link "Cliquer Projet dans Kanban" → ouvre DIRECT
  // le detail de la vidéo (modal), sans animation vers le projet.
  useEffect(() => {
    if (loading || !tasks.length) return;
    let target = null;
    try { target = localStorage.getItem('asa_open_task_row'); } catch { /* noop */ }
    if (!target) { setPendingDeepLink(false); return; }
    try { localStorage.removeItem('asa_open_task_row'); } catch { /* noop */ }
    const t = tasks.find((x) => x.id === target);
    if (t) {
      setSelectedTask(t);
      // Petite pause pour laisser le modal se peindre avant de retirer l'overlay
      setTimeout(() => setPendingDeepLink(false), 50);
    } else {
      setPendingDeepLink(false);
      setTimeout(() => {
        const el = document.getElementById(`task-row-${target}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [loading, tasks]);

  const [dragFromIdx, setDragFromIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null); // [task_id, ...] or null
  const [savingOrder, setSavingOrder] = useState(false);
  const [showHistory, setShowHistory] = useState(false); // Iter 62cs — rollback modal
  const rootTasks = useMemo(
    () => tasks
      // Show: macros (manual/framework) + top-level video tasks (kind='video', no parent)
      .filter((t) => !t.parent_task_id && (t.is_macro || t.kind === 'video'))
      .sort((a, b) => {
        // Videos first, macros after
        const av = a.kind === 'video' ? 0 : 1;
        const bv = b.kind === 'video' ? 0 : 1;
        if (av !== bv) return av - bv;
        if (a.kind === 'video' && b.kind === 'video') {
          // Iter 62dz — Rayan (2026-02-12) : "quand je drag & drop, l'ordre
          // doit changer visuellement IMMÉDIATEMENT". Si un pendingOrder est
          // actif (drag non encore sauvegardé), on tri par video_idx PURE
          // (car reorderVideos vient de le mettre à jour). Sinon fallback
          // chronologique (due_date) comme avant pour rester aligné calendrier.
          if (pendingOrder) {
            return (a.video_idx ?? 0) - (b.video_idx ?? 0);
          }
          const da = a.due_date || '9999-12-31';
          const db_ = b.due_date || '9999-12-31';
          if (da !== db_) return da.localeCompare(db_);
          return (a.video_idx ?? 0) - (b.video_idx ?? 0);
        }
        const oa = a.order ?? Infinity;
        const ob = b.order ?? Infinity;
        if (oa !== ob) return oa - ob;
        return (a.created_at || '').localeCompare(b.created_at || '');
      }),
    [tasks, pendingOrder]
  );
  // Iter 62fo — FIX CRITIQUE : seules les vidéos avec stage='todo' (À faire) sont
  // comptabilisées dans le réordonnancement IA / manuel. Toutes les autres
  // (in_progress, review, to_post, done) sont EXCLUES comme si elles n'existaient
  // plus dans le projet. Fini le bug "Concept 4 / Concept 4 / Concept 4" causé
  // par l'IA qui essayait de ranger des vidéos déjà faites.
  const videoTasks = useMemo(() => tasks.filter((t) => t.kind === 'video' && !t.archived && (t.stage || 'todo') === 'todo'), [tasks]);
  // Children = explicit subtasks (parent_task_id) OR video deliverables linked to this macro (macro_ref_id)
  const childrenOf = (pid) =>
    tasks
      .filter((t) => t.parent_task_id === pid || t.macro_ref_id === pid)
      .sort((a, b) => (a.video_idx ?? 0) - (b.video_idx ?? 0));

  const total = rootTasks.filter((t) => !t.optional).length;
  const done = rootTasks.filter((t) => !t.optional && t.stage === 'done').length;
  // Avancement pondéré par étape (même calcul que la liste des projets)
  const STAGE_W = { todo: 0, in_progress: 0.25, review: 0.5, to_post: 0.75, done: 1 };
  const counted = rootTasks.filter((t) => !t.optional);
  const pct = total ? Math.round((counted.reduce((a, t) => a + (STAGE_W[t.stage || 'todo'] || 0), 0) / total) * 100) : 0;

  // Iter 62cl — Drag & drop reorder now requires an explicit "Enregistrer" click.
  // Previously the drop immediately POSTed to /reorder-videos → users lost their
  // custom order without validation + the backend was sliding dates with slots
  // which felt like an "auto-rollback". Now the reorder stays LOCAL until the
  // founder confirms via the Save bar.
  const reorderVideos = (fromId, toId) => {
    const videoRoots = rootTasks.filter((t) => t.kind === 'video' && (t.stage || 'todo') === 'todo');
    const fromIdx = videoRoots.findIndex((t) => t.id === fromId);
    const toIdx = videoRoots.findIndex((t) => t.id === toId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const next = [...videoRoots];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const orderById = new Map(next.map((t, i) => [t.id, i]));
    setTasks((prev) => prev.map((t) => (
      t.kind === 'video' && orderById.has(t.id)
        ? { ...t, video_idx: orderById.get(t.id) }
        : t
    )));
    setPendingOrder(next.map((t) => t.id));
  };
  // Vue liste : dépôt rapide = la file garde ses dates dans le nouvel ordre ; rester 3 s sur une vidéo = échange de dates.
  const dropListVideo = async (fromId, toId) => {
    const ready = swapReadyId === toId;
    clearListHover();
    setDragFromIdx(null); setDragOverIdx(null);
    if (!fromId || !toId || fromId === toId) return;
    const task = rootTasks.find((t) => t.id === fromId);
    const over = rootTasks.find((t) => t.id === toId);
    if (!task || !over || over.kind !== 'video') return;
    if (ready) {
      if (window.confirm('Est-ce que vous voulez faire un switch de date entre ces 2 vidéos ?')) swapVideoDates(task, over, handleTaskChange);
      return;
    }
    const pool = rootTasks.filter((t) => t.kind === 'video' && t.stage !== 'done' && t.stage !== 'to_post' && t.due_date)
      .sort((x, y) => x.due_date.localeCompare(y.due_date));
    if (task.due_date && over.due_date && await insertVideoAt(pool, task, over, handleTaskChange)) return;
    reorderVideos(fromId, toId);
  };
  const [swapReadyId, setSwapReadyId] = useState(null);
  const listHoverRef = useRef({ id: null, timer: null });
  const clearListHover = () => { clearTimeout(listHoverRef.current.timer); listHoverRef.current = { id: null, timer: null }; setSwapReadyId(null); };
  const hoverListVideo = (id) => {
    setDragOverIdx((prev) => (prev === id ? prev : id));
    if (id === listHoverRef.current.id) return;
    clearListHover();
    if (!id || id === dragFromIdx) return;
    listHoverRef.current = { id, timer: setTimeout(() => setSwapReadyId(id), 3000) };
  };
  const savePendingOrder = async () => {
    if (!pendingOrder || savingOrder) return;
    setSavingOrder(true);
    try {
      const r = await api.post(`/projects/${project.id}/reorder-videos`, {
        order: pendingOrder,
      });
      if (r?.data?.tasks?.length) {
        const byId = Object.fromEntries(r.data.tasks.map((t) => [t.id, t]));
        setTasks((prev) => prev.map((t) => (byId[t.id] ? { ...t, ...byId[t.id] } : t)));
      }
      toast.success('Ordre enregistré');
      setPendingOrder(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de réordonnancement');
    } finally {
      setSavingOrder(false);
    }
  };
  const cancelPendingOrder = () => {
    setPendingOrder(null);
    reload(); // revert to persisted video_idx from DB
  };
  const dragProps = {
    enable: true,
    fromId: dragFromIdx,
    overId: dragOverIdx,
    swapReadyId,
    onDragStart: (id) => setDragFromIdx(id),
    onDragOver: hoverListVideo,
    onDragLeave: (id) => setDragOverIdx((prev) => (prev === id ? null : prev)),
    onDrop: dropListVideo,
    onDragEnd: () => { clearListHover(); setDragFromIdx(null); setDragOverIdx(null); },
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer le projet "${project.name}" ?`)) return;
    try { await api.delete(`/projects/${project.id}`); toast.success('Projet supprimé'); onDelete(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="page-fade">
      {/* Iter 62do — Overlay noir plein écran tant que le deep-link n'a pas ouvert
          le modal vidéo. Évite le flash de "chargement du projet". */}
      {pendingDeepLink && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: '#050914',
          display: 'grid', placeItems: 'center',
        }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid rgba(91,190,252,0.25)', borderTopColor: '#5BBEFC', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {/* Iter 62cl — Pending reorder confirmation bar (drag & drop safety) */}
      {pendingOrder && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          zIndex: 90, display: 'flex', alignItems: 'center', gap: 14,
          padding: '14px 20px', borderRadius: 18,
          background: 'linear-gradient(140deg, rgba(30,95,255,0.16) 0%, rgba(15,25,60,0.94) 55%, rgba(10,20,45,0.98) 100%)',
          backdropFilter: 'blur(18px)',
          border: '1.5px solid rgba(91,190,252,0.55)',
          boxShadow: '0 18px 50px rgba(30,95,255,0.35), 0 0 0 1px rgba(255,255,255,0.04) inset',
          fontFamily: 'Sora',
        }}>
          <span aria-hidden style={{
            width: 32, height: 32, borderRadius: 10,
            display: 'grid', placeItems: 'center',
            background: 'rgba(91,190,252,0.20)',
            border: '1px solid rgba(91,190,252,0.55)',
            color: '#5BBEFC',
            flexShrink: 0,
          }}>
            <GripVertical size={16} strokeWidth={2.2} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginRight: 8 }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: '#5BBEFC', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              Elevate Influence
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--t1)', lineHeight: 1.1 }}>
              Ordre modifié — enregistrer ?
            </div>
          </div>
          <button
            onClick={cancelPendingOrder}
            data-testid="cancel-reorder-btn"
            disabled={savingOrder}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '10px 16px', borderRadius: 11,
              background: 'rgba(255,255,255,0.04)', border: '1.5px solid var(--bd)',
              color: 'var(--t2)', fontFamily: 'Sora', fontSize: 13, fontWeight: 700,
              cursor: savingOrder ? 'not-allowed' : 'pointer',
              opacity: savingOrder ? 0.5 : 1,
              transition: 'all 0.15s var(--ease-out)',
            }}
          >
            <X size={15} /> Annuler
          </button>
          <button
            onClick={savePendingOrder}
            data-testid="save-reorder-btn"
            disabled={savingOrder}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '12px 22px', borderRadius: 11,
              background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 55%, #1E5FFF 100%)',
              border: '1.5px solid rgba(255,255,255,0.15)',
              color: '#FFFFFF',
              fontFamily: 'Sora', fontSize: 14, fontWeight: 800, letterSpacing: 0.2,
              cursor: savingOrder ? 'wait' : 'pointer',
              boxShadow: '0 8px 24px rgba(30,95,255,0.55), 0 0 0 1px rgba(91,190,252,0.35) inset',
              transition: 'all 0.15s var(--ease-out)',
            }}
          >
            {savingOrder ? <Loader2 size={15} className="spin" /> : <Check size={15} strokeWidth={2.4} />}
            {savingOrder ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}
      {/* Header */}
      <div style={{ padding: '22px 22px 14px' }}>
        <button onClick={onBack} data-testid="project-back-btn" style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          color: 'var(--p)', fontFamily: 'Sora', fontWeight: 600, fontSize: '0.82rem',
          marginBottom: 16, padding: 4,
        }}>
          <ArrowLeft size={15} /> Projets
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: `${project.color}1A`,
            border: `1.5px solid ${project.color}55`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <ProjectIcon name={project.icon} size={26} color={project.color} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="asa-display" style={{ fontSize: 'var(--text-2xl)', margin: 0, color: 'var(--t1)' }}>
              {project.name}
            </h1>
            {project.client && (
              <p style={{ fontFamily: 'Lexend', fontSize: '0.88rem', color: 'var(--t3)', margin: '4px 0 0' }}>
                {project.client}
              </p>
            )}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, maxWidth: 280, height: 6, borderRadius: 3, background: 'var(--sf2)', overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`, height: '100%',
                  background: `linear-gradient(90deg, ${project.color}, ${project.color}AA)`,
                  transition: 'width 0.4s var(--ease-out)',
                }} />
              </div>
              <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.84rem', color: project.color }}>{pct}%</span>
              <span style={{ fontFamily: 'Lexend', fontSize: '0.72rem', color: 'var(--t3)' }}>
                {done}/{total}
              </span>
            </div>
            {/* Iter42 (post-deploy) — project metadata bar */}
            <ProjectMetaBar project={project} tasks={tasks} users={users} />
          </div>
          {isFounder && (
            <>
              {/* Iter 62cs — Rollback history clock button (founder only) */}
              <button
                onClick={() => setShowHistory(true)}
                data-testid="project-history-btn"
                title="Historique des ordres — rollback"
                style={{
                  padding: 8, borderRadius: 10,
                  background: 'linear-gradient(135deg, rgba(91,190,252,0.16), rgba(30,95,255,0.18))',
                  border: '1.5px solid rgba(91,190,252,0.45)',
                }}
              >
                <Clock size={14} color="#5BBEFC" />
              </button>
              <button
                onClick={() => setShowEdit(true)}
                data-testid="project-edit-btn"
                title="Modifier le projet"
                style={{
                  padding: 8, borderRadius: 10,
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                }}
              >
                <Pencil size={14} color="var(--t2)" />
              </button>
              <button onClick={remove} data-testid="project-delete-btn" style={{
                padding: 8, borderRadius: 10,
                background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
              }}>
                <Trash2 size={14} color="var(--r)" />
              </button>
            </>
          )}
        </div>

        {/* === Global Links bar (Drive / SwissTransfer / external) === */}
        <ProjectLinksBar project={project} />

        {/* === Assignees pills === */}
        {(project.assignees || []).length > 0 && (
          <div style={{
            marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          }}>
            <span className="asa-eyebrow" style={{ marginRight: 4 }}>Assignés :</span>
            {(project.assignees || []).map((uid) => {
              const u = users.find((x) => x.id === uid);
              if (!u) return null;
              return (
                <span
                  key={uid}
                  data-testid={`project-assignee-pill-${uid}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '3px 9px 3px 4px', borderRadius: 999,
                    background: `${u.color}1A`, border: `1.5px solid ${u.color}55`,
                    color: u.color, fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5,
                  }}
                >
                  <Avatar user={u} size={16} />
                  {u.name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Inspirations panel */}
      <div style={{ padding: '0 22px 12px' }}>
        <InspirationsPanel
          project={project}
          users={users}
          onAdd={() => setShowInspirationModal(true)}
          onRemoved={reload}
        />
      </div>

      {/* Flowchart task list */}
      <div style={{ padding: '0 22px 100px' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="shimmer" style={{ height: 80, borderRadius: 14 }} />)}
          </div>
        ) : rootTasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--t3)' }}>
            {isFounder ? (
              <>
                <div className="t-display-sm">Aucune tâche dans ce projet</div>
                <button onClick={() => setShowCreate(true)} className="asa-btn" style={{ marginTop: 14 }}>
                  <Plus size={14} /> Ajouter une tâche
                </button>
              </>
            ) : (
              <>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(245,166,35,0.10)', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                  <Clock size={26} color="#F5A623" strokeWidth={1.5} />
                </div>
                <div className="t-display-sm" style={{ color: 'var(--t1)' }}>Aucune tâche assignée pour le moment</div>
                <div style={{ marginTop: 8, fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', maxWidth: 380, margin: '8px auto 0', lineHeight: 1.5 }}>
                  Tu es bien sur ce projet, mais les founders n&apos;ont pas encore généré les vidéos à monter. Reviens plus tard ou demande-leur de te dispatcher des tâches.
                </div>
              </>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative', paddingLeft: 22 }}>
            {/* Vertical line */}
            <div style={{
              position: 'absolute', left: 18, top: 22, bottom: 22,
              width: 2, background: 'var(--bd)', borderRadius: 1,
            }} />
            {/* Iter 62fl — Toggle Vue : Liste / Kanban + "Grouper par format" (liste uniquement) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10, background: 'var(--sf)', border: '1px solid var(--bd)' }}>
                {[
                  { id: 'list',   label: 'Liste' },
                  { id: 'kanban', label: 'Kanban' },
                ].map((m) => {
                  const active = viewMode === m.id;
                  return (
                    <button
                      key={m.id}
                      data-testid={`project-view-${m.id}`}
                      onClick={() => setViewMode(m.id)}
                      style={{
                        padding: '6px 14px', borderRadius: 7, border: 'none',
                        background: active ? 'linear-gradient(180deg, #5BBEFC 0%, #3E7BFA 100%)' : 'transparent',
                        color: active ? '#0B1330' : 'var(--t2)',
                        fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5,
                        cursor: 'pointer', letterSpacing: '-0.005em',
                        boxShadow: active ? '0 4px 10px -3px rgba(62,123,250,0.45), inset 0 1px 0 rgba(255,255,255,0.3)' : 'none',
                      }}
                    >{m.label}</button>
                  );
                })}
              </div>
              {(() => {
                const n = rootTasks.filter(badPub).length;
                return (n > 0 || titleFilter) && (
                  <button type="button" data-testid="filter-titles-to-fix" onClick={() => setTitleFilter((x) => !x)} style={{
                    padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                    background: titleFilter ? 'rgba(248,113,113,0.16)' : 'transparent',
                    border: `1px solid ${titleFilter ? '#F87171' : 'rgba(248,113,113,0.45)'}`,
                    color: '#F87171', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                  }}>{titleFilter ? '✓ ' : '⚠️ '}Titres à corriger ({n})</button>
                );
              })()}
              {viewMode === 'list' && (() => {
                const uniqueFormats = new Set(rootTasks.filter((t) => t.kind === 'video' && t.video_format).map((t) => t.video_format));
                return uniqueFormats.size >= 2 && (
                  <button
                    type="button"
                    data-testid="toggle-group-by-format"
                    onClick={() => setGroupByFormat(!groupByFormat)}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: groupByFormat ? 'rgba(167,139,250,0.16)' : 'transparent',
                      border: `1px solid ${groupByFormat ? '#B294F5' : 'var(--bd)'}`,
                      color: groupByFormat ? '#B294F5' : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                      cursor: 'pointer', letterSpacing: '-0.005em',
                    }}
                  >{groupByFormat ? '✓ ' : ''}Grouper par format</button>
                );
              })()}
            </div>
            {viewMode === 'kanban' ? (
              <ProjectKanban
                tasks={rootTasks.filter((t) => t.kind === 'video' && (!titleFilter || badPub(t)))}
                onChanged={handleTaskChange}
                onEdit={setSelectedTask}
              />
            ) : (
            <div className="stagger" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(() => {
                // Iter 62fm — Masquer les "À poster" de la liste principale : ce sont des tâches, plus des vidéos à ranger.
                const displayedTasks = titleFilter ? rootTasks.filter(badPub) : rootTasks.filter((t) => t.stage !== 'to_post');
                if (!groupByFormat) {
                  return displayedTasks.map((t, idx) => (
                    <FlowTaskRow
                      key={t.id}
                      idx={idx + 1}
                      task={t}
                      users={users}
                      project={project}
                      concepts={concepts}
                      expanded={expanded[t.id]}
                      onToggle={() => setExpanded({ ...expanded, [t.id]: !expanded[t.id] })}
                      childRows={childrenOf(t.id)}
                      onChanged={handleTaskChange}
                      onEdit={setSelectedTask}
                      dragProps={dragProps}
                    />
                  ));
                }
                // Groupement par format (Iter 62ea)
                const groups = new Map();
                displayedTasks.forEach((t) => {
                  const key = t.kind === 'video' ? (t.video_format || 'Autres') : 'Étapes';
                  if (!groups.has(key)) groups.set(key, []);
                  groups.get(key).push(t);
                });
                // Ordre : formats vidéo triés alpha, puis "Autres", puis "Étapes" (macros)
                const orderedKeys = Array.from(groups.keys()).sort((a, b) => {
                  if (a === 'Étapes') return 1; if (b === 'Étapes') return -1;
                  if (a === 'Autres') return 1; if (b === 'Autres') return -1;
                  return a.localeCompare(b);
                });
                let globalIdx = 0;
                const nodes = [];
                orderedKeys.forEach((k) => {
                  const items = groups.get(k);
                  nodes.push(
                    <div key={`hdr-${k}`} data-testid={`group-hdr-${k.replace(/\s+/g, '-').toLowerCase()}`} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      marginTop: 6, marginBottom: -4,
                      fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
                      color: k === 'Étapes' ? 'var(--t3)' : '#B294F5',
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>
                      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${k === 'Étapes' ? 'var(--bd)' : 'rgba(167,139,250,0.35)'} 30%, ${k === 'Étapes' ? 'var(--bd)' : 'rgba(167,139,250,0.35)'} 70%, transparent)` }} />
                      <span>{k} <span style={{ fontWeight: 700, opacity: 0.7 }}>({items.length})</span></span>
                      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, transparent, ${k === 'Étapes' ? 'var(--bd)' : 'rgba(167,139,250,0.35)'} 30%, ${k === 'Étapes' ? 'var(--bd)' : 'rgba(167,139,250,0.35)'} 70%, transparent)` }} />
                    </div>
                  );
                  items.forEach((t) => {
                    globalIdx++;
                    nodes.push(
                      <FlowTaskRow
                        key={t.id}
                        idx={globalIdx}
                        task={t}
                        users={users}
                        project={project}
                        concepts={concepts}
                        expanded={expanded[t.id]}
                        onToggle={() => setExpanded({ ...expanded, [t.id]: !expanded[t.id] })}
                        childRows={childrenOf(t.id)}
                        onChanged={handleTaskChange}
                        onEdit={setSelectedTask}
                        dragProps={dragProps}
                      />
                    );
                  });
                });
                return nodes;
              })()}
            </div>
            )}
          </div>
        )}

        {!loading && rootTasks.length > 0 && isFounder && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              data-testid="project-add-video"
              onClick={() => setShowAddVideo(true)}
              style={{
                flex: 1, minWidth: 180, padding: '12px 16px',
                background: 'rgba(167,139,250,0.10)',
                border: '1.5px dashed #A78BFA',
                borderRadius: 12, color: '#A78BFA',
                fontFamily: 'Sora', fontWeight: 700, fontSize: '0.84rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer',
              }}
            >
              <Video size={14} /> Ajouter une vidéo
            </button>
            <ActionMenu
              testid="project-menu-rangement" label="Rangement" icon={<Sparkles size={14} />} rgb="167,139,250"
              items={[
                { testid: 'project-ai-rearrange', label: '✨ Rangement par IA', onClick: () => setShowAIRearrange(true), disabled: videoTasks.length === 0 },
                { testid: 'project-calendar', label: '📅 Calendrier', onClick: () => setShowCalendar(true) },
                { testid: 'project-replan', label: `🕒 Réorganiser · Replanifier (${videoTasks.length})`, onClick: () => setShowReplan(true), hidden: videoTasks.length === 0 },
              ]}
            />
            <ActionMenu
              testid="project-menu-post" label="Post" icon={<span>📱</span>} rgb="245,197,106"
              items={[
                { testid: 'project-add-postage', label: '📱 Ajouter le postage (titres · descriptions · Drive)', onClick: () => setShowPostage(true) },
                { testid: 'project-post-ai-rearrange', label: '✨ Rangement des posts par IA', onClick: () => setShowAIRearrange('post'), hidden: !tasks.some((t) => t.stage === 'to_post' && !t.archived) },
                { testid: 'project-post-order', label: `↕ Ordre des posts · rythme (${tasks.filter((t) => t.stage === 'to_post' && !t.archived).length})`, onClick: () => setShowPostOrder(true), hidden: !tasks.some((t) => t.stage === 'to_post' && !t.archived) },
                { testid: 'project-transfer-poster', label: '⇄ Modifier le poster', onClick: () => setShowTransferPoster(true) },
                { testid: 'project-pub-check', label: '🔎 Vérifier les titres & descriptions', onClick: () => setShowPubCheck(true) },
                { testid: 'project-covers', label: `🖼 Couvertures${project.covers_enabled ? ' · activées' : ''}`, onClick: () => setShowCovers(true) },
                { testid: 'project-post-drive-link', label: '🔗 Lien Drive des posts', onClick: async () => {
                  const url = window.prompt('Lien Drive des posts pour TOUT le projet (appliqué à toutes les vidéos pas encore postées) :', project.post_drive_link || '');
                  if (url === null) return;
                  try {
                    const r = await api.post(`/projects/${project.id}/post-drive-link`, { url });
                    toast.success(`Lien Drive appliqué à ${r.data.videos} vidéo(s) ✓`);
                    reload();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                } },
                { testid: project.postage_removed ? 'project-postage-restore' : 'project-postage-remove', hidden: !(project.postage_removed || tasks.some((t) => t.publication_title || t.publication_description)),
                  label: project.postage_removed ? '↺ Remettre le postage' : '✕ Retirer le postage', danger: !project.postage_removed, onClick: async () => {
                    const restore = !!project.postage_removed;
                    if (!restore && !window.confirm('Retirer le postage (titres, descriptions) de toutes les vidéos ? Tu pourras le remettre.')) return;
                    try {
                      const r = await api.post(`/projects/${project.id}/postage/${restore ? 'restore' : 'remove'}`);
                      toast.success(restore ? `Postage remis ✓ (${r.data.restored} vidéos)` : `Postage retiré (${r.data.removed} vidéos) — tu peux le remettre`);
                      setProject((pr) => ({ ...pr, postage_removed: !restore }));
                      reload();
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                  } },
              ]}
            />
          </div>
        )}
      </div>

      {showAddVideo && (
        <AddVideoDialog
          project={project}
          onClose={() => setShowAddVideo(false)}
          onSaved={() => { setShowAddVideo(false); reload(); }}
        />
      )}

      {/* Iter 62et — Rayan msg : "enlève IA doc, mets Rangement par IA".
          Le rangement par IA lit les vidéos existantes et propose une nouvelle
          distribution de dates (et d'ordre si demandé) via Claude Sonnet 4.5. */}
      {showAIRearrange && (
        <AIRearrangeDialog
          project={project}
          scope={showAIRearrange === 'post' ? 'post' : undefined}
          onClose={() => setShowAIRearrange(false)}
          onApplied={() => { setShowAIRearrange(false); reload(); }}
        />
      )}

      {showPostOrder && (
        <PostOrderDialog
          project={project}
          tasks={tasks}
          onClose={() => setShowPostOrder(false)}
          onSaved={() => { setShowPostOrder(false); reload(); }}
        />
      )}
      {showPubCheck && <PublicationCheckDialog project={project} initialRows={pubCheckRows} onClose={() => { setShowPubCheck(false); setPubCheckRows(null); }} onSaved={reload} />}
      {showCovers && <CoversDialog project={project} onClose={() => setShowCovers(false)} onSaved={() => { setShowCovers(false); reload(); }} />}
      {showTransferPoster && (
        <TransferPosterDialog
          project={project}
          tasks={tasks}
          users={users}
          onClose={() => setShowTransferPoster(false)}
          onSaved={() => { setShowTransferPoster(false); reload(); }}
        />
      )}
      {showPostage && (
        <ProjectPostageDialog
          project={project}
          users={users}
          onClose={() => setShowPostage(false)}
          onSaved={() => {
            setShowPostage(false); reload();
            // Vérification automatique des titres après chaque import de doc
            toast.info('🔎 Vérification des titres en cours…');
            api.post(`/projects/${project.id}/publication-check`, {}, { timeout: 120000 }).then((r) => {
              if (r.data.errors) { toast.warning(`⚠️ ${r.data.errors} titre(s)/description(s) à corriger`); setPubCheckRows(r.data.rows); setShowPubCheck(true); }
              else toast.success('✓ Titres vérifiés : tout est sur la bonne vidéo');
            }).catch(() => {});
          }}
        />
      )}
      {showReplan && (
        <ReplanDialog
          project={project}
          videos={videoTasks}
          users={users}
          onClose={() => setShowReplan(false)}
          onSaved={() => { setShowReplan(false); reload(); }}
        />
      )}

      {showCalendar && (
        <ProjectCalendarDialog
          project={project}
          tasks={tasks}
          onClose={() => setShowCalendar(false)}
          onChanged={handleTaskChange}
        />
      )}

      {showCreate && (
        <SubTaskDialog
          projectId={project.id}
          projectAssignees={project.assignees || []}
          users={users}
          existingCount={rootTasks.length}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); reload(); }}
        />
      )}

      {showInspirationModal && (
        <InspirationModal
          project={project}
          onClose={() => setShowInspirationModal(false)}
          onSaved={() => { setShowInspirationModal(false); reload(); }}
        />
      )}

      {selectedTask && (
        <DetailSidebar
          task={selectedTask}
          users={users}
          isFounder={isFounder}
          onClose={() => setSelectedTask(null)}
          onSaved={(updated) => {
            // Iter42 fix — optimistic update. When flipStep / PATCH returns the fresh task,
            // patch selectedTask immediately so the sidebar re-renders the next step.
            // Without this, the user had to close & reopen the sidebar to see the update
            // (bug reported live by user 2026-02-28).
            if (updated && updated.id) {
              setSelectedTask((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
              setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
            } else {
              reload();
            }
          }}
          onDelete={() => { reload(); setSelectedTask(null); }}
        />
      )}

      {showEdit && (
        <ProjectEditDialog
          project={project}
          users={users}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); reload(); }}
        />
      )}

      {showHistory && (
        <ReorderHistoryModal
          project={project}
          onClose={() => setShowHistory(false)}
          onRestored={() => { setShowHistory(false); reload(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ReorderHistoryModal (Iter 62cs) — Rollback UI
// Lists every reorder snapshot for the project. Founders can restore any past
// order in one click. Each restore itself creates a snapshot → fully reversible.
// ============================================================================
function ReorderHistoryModal({ project, onClose, onRestored }) {
  const [snaps, setSnaps] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/projects/${project.id}/reorder-history`);
        setSnaps(r.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Impossible de charger l\'historique');
        setSnaps([]);
      }
    })();
  }, [project.id]);

  const restore = async (snap) => {
    if (!window.confirm(`Restaurer cet ordre du ${new Date(snap.created_at).toLocaleString('fr-FR')} ?\n\nLe rollback est réversible — l'ordre actuel sera aussi sauvegardé.`)) return;
    setBusyId(snap.id);
    try {
      await api.post(`/projects/${project.id}/reorder-restore/${snap.id}`);
      toast.success('Ordre restauré');
      onRestored();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de restauration');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(3, 8, 22, 0.72)',
        backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560, maxHeight: '80vh', overflow: 'auto',
          borderRadius: 20, padding: 22,
          background: 'linear-gradient(160deg, rgba(30,95,255,0.10) 0%, var(--sf) 45%)',
          border: '1.5px solid rgba(91,190,252,0.35)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Clock size={20} color="#5BBEFC" />
            <div>
              <div style={{ fontSize: 9.5, fontWeight: 800, color: '#5BBEFC', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                Elevate Influence
              </div>
              <div className="asa-display" style={{ fontSize: 18, color: 'var(--t1)' }}>
                Historique des ordres
              </div>
            </div>
          </div>
          <button onClick={onClose} className="asa-btn-ghost asa-btn" style={{ padding: 6 }}>
            <X size={14} />
          </button>
        </div>
        <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '8px 0 16px' }}>
          Clique sur un ordre pour restaurer la disposition de ce moment-là. Chaque restauration est elle-même snapshotée, donc totalement réversible.
        </p>

        {snaps === null && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t3)' }}>
            <Loader2 size={20} className="spin" />
          </div>
        )}

        {snaps && snaps.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 13 }}>
            Aucun changement d&apos;ordre pour le moment.<br />
            Fais un drag &amp; drop puis clique &laquo;&nbsp;Enregistrer&nbsp;&raquo; pour créer une première entrée.
          </div>
        )}

        {snaps && snaps.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {snaps.map((snap) => {
              const dt = new Date(snap.created_at);
              const preview = (snap.titles || []).slice(0, 4).join(' · ');
              return (
                <div
                  key={snap.id}
                  data-testid={`history-snap-${snap.id}`}
                  style={{
                    padding: '12px 14px', borderRadius: 12,
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)' }}>
                        {dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} · {dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span style={{
                        fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
                        background: snap.reason === 'before_restore' ? 'rgba(245,158,11,0.15)' : 'rgba(91,190,252,0.15)',
                        color: snap.reason === 'before_restore' ? '#F59E0B' : '#5BBEFC',
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                      }}>{snap.reason === 'before_restore' ? 'Avant rollback' : 'Avant reorder'}</span>
                    </div>
                    <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginBottom: 4 }}>
                      par {snap.created_by_name || 'inconnu'} · {snap.order?.length || 0} vidéos
                    </div>
                    <div style={{
                      fontFamily: 'Lexend', fontSize: 11, color: 'var(--t2)',
                      overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.35,
                    }}>
                      {preview || '—'}
                    </div>
                  </div>
                  <button
                    onClick={() => restore(snap)}
                    disabled={busyId === snap.id}
                    data-testid={`restore-snap-${snap.id}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '8px 14px', borderRadius: 10, flexShrink: 0,
                      background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 55%, #1E5FFF 100%)',
                      border: 'none', color: '#FFFFFF',
                      fontFamily: 'Sora', fontSize: 12, fontWeight: 800, letterSpacing: 0.2,
                      cursor: busyId === snap.id ? 'wait' : 'pointer',
                      boxShadow: '0 4px 12px rgba(30,95,255,0.35)',
                    }}
                  >
                    {busyId === snap.id ? <Loader2 size={12} className="spin" /> : <ArrowLeft size={12} />}
                    Restaurer
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// ProjectMetaBar (Iter42 post-deploy)
// ---------------------------------------------------------------------------
// Shows the project creation date + the last validated video (title + by whom + when).
// Small helpful metadata so founders/monteurs immediately know:
//   - "Quand ce projet a été créé"
//   - "Quelle vidéo a été validée en dernier + par qui"
// ============================================================================
function ProjectMetaBar({ project, tasks, users }) {
  const created = project?.created_at || project?.updated_at;
  // Find the most recently frame-validated video task
  const validatedVideos = (tasks || []).filter(
    (t) => t.kind === 'video' && (t.frame_validated || t.posted_online)
  );
  validatedVideos.sort((a, b) => {
    const ta = a.frame_validated_at || a.updated_at || '';
    const tb = b.frame_validated_at || b.updated_at || '';
    return tb.localeCompare(ta);
  });
  const lastValidated = validatedVideos[0];
  const validatedByUser = lastValidated?.frame_validated_by
    ? users.find((u) => u.id === lastValidated.frame_validated_by)
    : null;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'short', year: 'numeric',
      });
    } catch { return iso; }
  };
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div
      data-testid="project-meta-bar"
      style={{
        marginTop: 12,
        display: 'flex', flexWrap: 'wrap', gap: 16,
        fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)',
      }}
    >
      <div data-testid="project-meta-created">
        <span style={{ color: 'var(--t3)' }}>📅 Créé le </span>
        <strong style={{ color: 'var(--t2)' }}>{fmtDate(created)}</strong>
      </div>
      {lastValidated ? (
        <div data-testid="project-meta-last-validated">
          <span style={{ color: 'var(--t3)' }}>✓ Dernière vidéo validée : </span>
          <strong style={{ color: '#34D399' }}>{lastValidated.title}</strong>
          {validatedByUser && (
            <>
              <span style={{ color: 'var(--t3)' }}> par </span>
              <strong style={{ color: 'var(--t2)' }}>{validatedByUser.name}</strong>
            </>
          )}
          {lastValidated.frame_validated_at && (
            <>
              <span style={{ color: 'var(--t3)' }}> · </span>
              <strong style={{ color: 'var(--t2)' }}>{fmtDateTime(lastValidated.frame_validated_at)}</strong>
            </>
          )}
        </div>
      ) : (
        <div data-testid="project-meta-no-validation" style={{ color: 'var(--t3)' }}>
          Aucune vidéo validée pour le moment
        </div>
      )}
    </div>
  );
}

function FlowTaskRow({ idx, task, users, project, concepts = [], expanded, onToggle, childRows, onChanged, onEdit, dragProps }) {
  const nav = useNavigate();
  const s = STAGES[task.stage] || STAGES.todo;
  const assignee = users.find((u) => u.id === task.assigned_to);
  const isFichesMacro = task.action_url === '#fiches' || (task.title || '').toLowerCase().includes('fiche de poste');
  const hasChildren = childRows.length > 0 || isFichesMacro;
  const isVideo = task.kind === 'video';
  const canDrag = isVideo && !!dragProps?.enable;

  const setStage = async (newStage) => {
    const extra = await stageDeliveryExtra(task, newStage, CAN_MANAGE.current);
    if (!extra) return;
    try { await api.patch(`/tasks/${task.id}`, { stage: newStage, ...extra }); onChanged(); }
    catch { toast.error('Erreur'); }
  };

  // Iter42c — drag & drop wiring (only for video tasks)
  const dragHandlers = canDrag ? {
    draggable: true,
    onDragStart: (e) => {
      dragProps.onDragStart(task.id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      dragProps.onDragOver(task.id);
    },
    onDragLeave: () => dragProps.onDragLeave(task.id),
    onDrop: (e) => {
      e.preventDefault();
      dragProps.onDrop(dragProps.fromId || e.dataTransfer.getData('text/plain'), task.id);
    },
    onDragEnd: () => dragProps.onDragEnd(),
  } : {};
  const isDraggedOver = canDrag && dragProps.overId === task.id && dragProps.fromId !== task.id;
  const isDragging = canDrag && dragProps.fromId === task.id;
  const swapReady = canDrag && dragProps.swapReadyId === task.id;

  // Iter42e — inline title edit (double-click or pencil icon)
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title || '');
  useEffect(() => { if (!titleEditing) setTitleDraft(task.title || ''); }, [task.title, titleEditing]);
  const saveTitle = async () => {
    const clean = titleDraft.trim();
    if (!clean || clean === task.title) { setTitleEditing(false); return; }
    try {
      const r = await api.patch(`/tasks/${task.id}`, { title: clean });
      toast.success('Titre mis à jour');
      onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setTitleEditing(false);
    }
  };

  return (
    <div id={`task-row-${task.id}`} style={{ position: 'relative', borderRadius: 12 }} {...dragHandlers}>
      {/* Horizontal connector */}
      <div style={{
        position: 'absolute', left: -22, top: 28,
        width: 18, height: 2, background: 'var(--bd)',
      }} />
      {/* Node dot */}
      <div style={{
        position: 'absolute', left: -28, top: 22,
        width: 14, height: 14, borderRadius: '50%',
        background: 'var(--bg)',
        border: `2px solid ${s.color}`,
        display: 'grid', placeItems: 'center',
      }}>
        {task.stage === 'done' && <Check size={8} color={s.color} strokeWidth={3} />}
      </div>

      <div className="asa-card" style={{
        padding: '14px 16px',
        border: swapReady ? '2px solid #34D399' : isDraggedOver ? '1.5px dashed #5BBEFC' : undefined,
        background: swapReady ? 'rgba(52,211,153,0.08)' : isDraggedOver ? 'rgba(91,190,252,0.06)' : undefined,
        opacity: isDragging ? 0.4 : 1,
        cursor: canDrag ? 'grab' : undefined,
        transition: 'opacity 0.12s, border-color 0.12s, background 0.12s',
      }}>
        {swapReady && <div data-testid={`list-swap-hint-${task.id}`} style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '0.7rem', color: '#34D399', marginBottom: 6 }}>⇄ Relâche pour échanger les dates</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Iter42c — drag handle (video tasks only) */}
          {canDrag && (
            <div
              data-testid={`flow-drag-handle-${task.id}`}
              style={{
                width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--t3)', cursor: 'grab',
              }}
              title="Glisse pour réordonner"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <GripVertical size={14} />
            </div>
          )}
          {/* Index */}
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'var(--sf2)', border: `1.5px solid ${s.color}66`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
            fontFamily: 'Sora', fontWeight: 800, fontSize: '0.78rem', color: s.color,
          }}>
            {String(idx).padStart(2, '0')}
          </div>

          {/* Title + meta */}
          <div
            onClick={(e) => {
              // Priority: if has children → toggle; else → open editor
              if (hasChildren) {
                onToggle();
              } else if (onEdit) {
                onEdit(task);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              flex: 1, minWidth: 0, textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div
              data-testid={`task-title-${task.id}`}
              style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)',
                textTransform: 'uppercase', letterSpacing: '0.02em',
                marginBottom: 4,
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {titleEditing ? (
                <input
                  data-testid={`task-title-input-${task.id}`}
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveTitle();
                    else if (e.key === 'Escape') { setTitleEditing(false); setTitleDraft(task.title || ''); }
                  }}
                  onBlur={saveTitle}
                  draggable={false}
                  style={{
                    flex: 1, minWidth: 0,
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)',
                    textTransform: 'uppercase', letterSpacing: '0.02em',
                    padding: '4px 8px',
                    background: 'var(--sf2)', border: '1.5px solid #5BBEFC', borderRadius: 6,
                  }}
                />
              ) : (
                <>
                  <span
                    onDoubleClick={(e) => { e.stopPropagation(); setTitleEditing(true); }}
                    title="Double-clic pour renommer"
                    style={{ cursor: 'text' }}
                  >
                    {task.title}
                  </span>
                  <button
                    data-testid={`task-title-edit-btn-${task.id}`}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTitleEditing(true); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label="Renommer la vidéo"
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: 6,
                      background: 'transparent', border: '1px solid transparent',
                      color: 'var(--t3)', cursor: 'pointer',
                      opacity: 0.5, transition: 'opacity 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.borderColor = 'var(--bd)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.borderColor = 'transparent'; }}
                  >
                    <Pencil size={11} />
                  </button>
                </>
              )}
            </div>
            {task.description && (
              <div style={{ fontFamily: 'Lexend', fontSize: '0.76rem', color: 'var(--t3)', marginBottom: 4 }}>
                {task.description}
              </div>
            )}
            {/* Iter47s — Concept title chip under the video title (option A) */}
            {task.concept_id && (() => {
              const concept = concepts.find((c) => c.id === task.concept_id);
              if (!concept) return null;
              return (
                <div
                  data-testid={`task-concept-title-${task.id}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.72rem',
                    color: '#F5A623', letterSpacing: '0.01em',
                    padding: '3px 9px', borderRadius: 999,
                    background: 'rgba(245,166,35,0.10)',
                    border: '1px solid rgba(245,166,35,0.30)',
                    marginBottom: 5,
                    maxWidth: '100%',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  title={concept.title}
                >
                  <Lightbulb size={9} strokeWidth={2} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {concept.title}
                  </span>
                </div>
              );
            })()}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StageDropdown
                task={task}
                onPickStage={async (newStage, extra = {}) => {
                  const dx = await stageDeliveryExtra(task, newStage, CAN_MANAGE.current);
                  if (!dx) return;
                  try {
                    await api.patch(`/tasks/${task.id}`, { stage: newStage, ...extra, ...dx });
                    toast.success(deliveryToast(dx) || (newStage === 'to_post' ? 'Vidéo prête à poster' : 'Statut mis à jour'));
                    onChanged();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                }}
              />
              {isVideo && <PubCheckBadge check={task.pub_check} />}
              {/* Iter41 (post-deploy) — pro format chip under each video title */}
              {task.kind === 'video' && task.video_format && (
                <span
                  data-testid={`task-format-chip-${task.id}`}
                  style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
                    padding: '3px 8px', borderRadius: 999,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    background: (task.video_format === 'podcast' ? 'rgba(167,139,250,0.16)'
                              : task.video_format === 'facecam' ? 'rgba(91,190,252,0.16)'
                              : task.video_format === 'short'   ? 'rgba(244,114,182,0.16)'
                              : 'rgba(148,163,184,0.16)'),
                    color: (task.video_format === 'podcast' ? '#A78BFA'
                          : task.video_format === 'facecam' ? '#5BBEFC'
                          : task.video_format === 'short'   ? '#F472B6'
                          : '#94A3B8'),
                  }}
                >
                  {task.video_format === 'podcast' ? 'Podcast'
                    : task.video_format === 'facecam' ? 'Facecam'
                    : task.video_format === 'short' ? 'Short'
                    : 'Spécial'}
                </span>
              )}
              {/* Iter42 (post-deploy) — Deadline chip. Very visible so monteurs never miss a due date. */}
              {(task.due_date || task.derush_deadline) && (() => {
                const dl = task.due_date || task.derush_deadline;
                const dlDate = new Date(dl);
                const now = new Date();
                const diffH = (dlDate - now) / (1000 * 60 * 60);
                const overdue = diffH < 0 && task.stage !== 'done';
                const soon = !overdue && diffH < 48;
                const color = overdue ? '#FF6B6B' : soon ? '#FBBF24' : '#5BBEFC';
                const bg = overdue ? 'rgba(255,107,107,0.14)' : soon ? 'rgba(251,191,36,0.14)' : 'rgba(91,190,252,0.14)';
                return (
                  <span
                    data-testid={`task-deadline-chip-${task.id}`}
                    style={{
                      fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem',
                      padding: '3px 8px', borderRadius: 999,
                      letterSpacing: '0.04em',
                      background: bg, color,
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                    title={dlDate.toLocaleString('fr-FR')}
                  >
                    <Calendar size={9} />
                    {overdue ? '⚠ En retard · ' : soon ? '⏱ Bientôt · ' : ''}
                    {dlDate.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {task.due_date && (
                      <span style={{ marginLeft: 3, opacity: 0.7 }}>
                        {dlDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </span>
                );
              })()}
              {hasChildren && !isFichesMacro && (
                <span style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: '0.66rem', color: 'var(--t3)' }}>
                  {childRows.length} sous-tâche{childRows.length > 1 ? 's' : ''}
                </span>
              )}
              {isFichesMacro && (
                <span style={{
                  fontFamily: 'Sora', fontWeight: 700, fontSize: '0.6rem',
                  padding: '3px 9px', borderRadius: 999,
                  background: 'rgba(245,166,35,0.13)', color: '#F5A623',
                  border: '1px solid rgba(245,166,35,0.3)',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>Optionnel</span>
              )}
              {task.action_label && (
                <button
                  data-testid={`task-action-${task.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Special case: open the group conversation
                    const isGroupAction =
                      task.action_url === '#group' ||
                      (task.action_label || '').toLowerCase().includes('ouvrir le groupe');
                    if (isGroupAction) {
                      if (project?.conversation_id) {
                        localStorage.setItem('asa_open_conv', project.conversation_id);
                        nav('/messages');
                      } else {
                        toast.error('Aucun groupe associé à ce projet');
                      }
                      return;
                    }
                    // External URL → open in new tab
                    if (task.action_url) {
                      window.open(task.action_url, '_blank', 'noopener');
                      return;
                    }
                    // No URL → prompt and save
                    const url = window.prompt(`URL pour "${task.action_label}" :`, '');
                    if (url) {
                      api.patch(`/tasks/${task.id}`, { action_url: url }).then(onChanged);
                      window.open(url, '_blank', 'noopener');
                    }
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontFamily: 'Sora', fontWeight: 600, fontSize: '0.66rem',
                    padding: '3px 9px', borderRadius: 999,
                    background: 'rgba(91, 190, 252, 0.10)',
                    color: 'var(--pl)',
                    border: '1px solid rgba(91, 190, 252, 0.25)',
                  }}
                >
                  <ArrowUpRight size={10} />
                  {task.action_label}
                </button>
              )}
            </div>
          </div>

          {/* Assignee + edit + chevron */}
          {assignee && <Avatar user={assignee} size={28} />}
          {onEdit && !isFichesMacro && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(task); }}
              data-testid={`flow-edit-${task.id}`}
              title="Modifier"
              style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                display: 'grid', placeItems: 'center', cursor: 'pointer',
              }}
            >
              <ArrowUpRight size={12} color="var(--t2)" />
            </button>
          )}
          {hasChildren && (
            <ChevronDown
              size={16}
              color="var(--t3)"
              style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}
            />
          )}
        </div>

        {/* Sub-tasks or Fiches editor */}
        {hasChildren && expanded && (
          <div style={{
            marginTop: 12, paddingLeft: 12, paddingTop: 10,
            borderTop: '1px solid var(--bd)',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            {isFichesMacro
              ? <FichesEditor project={project} onChanged={onChanged} />
              : childRows.map((c) => <SubTaskRow key={c.id} task={c} users={users} onChanged={onChanged} onEdit={onEdit} />)
            }
          </div>
        )}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// ProjectLinksBar — chips of all global Drive / SwissTransfer / external links at top of project
// ---------------------------------------------------------------------------
function ProjectLinksBar({ project }) {
  // Compose unified link list with kind
  const items = [];
  // Legacy single fields
  if (project.drive_link) items.push({ kind: 'drive', name: 'Drive', url: project.drive_link });
  if (project.swift_link) items.push({ kind: 'swift', name: 'SwissTransfer', url: project.swift_link });
  // New multi arrays
  (project.drive_links || []).forEach((l) => l.url && items.push({ kind: 'drive', name: l.name || 'Drive', url: l.url }));
  (project.swift_links || []).forEach((l) => l.url && items.push({ kind: 'swift', name: l.name || 'SwissTransfer', url: l.url }));
  // External (WhatsApp/Slack/etc.)
  if (project.external_link) items.push({ kind: 'external', name: 'Lien externe', url: project.external_link });
  // Concepts link (Montage framework)
  if (project.concepts_link) items.push({ kind: 'concepts', name: 'Drive concepts', url: project.concepts_link });

  if (items.length === 0) return null;

  const kindStyle = {
    drive:    { bg: 'rgba(46,163,242,0.10)',  bd: 'rgba(46,163,242,0.32)', co: '#5BBEFC' },
    swift:    { bg: 'rgba(167,139,250,0.10)', bd: 'rgba(167,139,250,0.35)', co: '#A78BFA' },
    external: { bg: 'rgba(52,211,153,0.10)',  bd: 'rgba(52,211,153,0.35)', co: '#34D399' },
    concepts: { bg: 'rgba(245,166,35,0.10)',  bd: 'rgba(245,166,35,0.35)', co: '#F5A623' },
  };

  return (
    <div
      data-testid="project-links-bar"
      style={{
        marginTop: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}
    >
      <span className="asa-eyebrow" style={{ marginRight: 4 }}>Liens :</span>
      {items.map((l, i) => {
        const s = kindStyle[l.kind] || kindStyle.external;
        const Icon = l.kind === 'drive' || l.kind === 'concepts' ? Folder : (l.kind === 'swift' ? LinkIcon : ExternalLink);
        return (
          <a
            key={`${l.url}-${i}`}
            href={l.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`project-link-${l.kind}-${i}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 11px 5px 9px', borderRadius: 999,
              background: s.bg, border: `1.5px solid ${s.bd}`,
              color: s.co, fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >
            <Icon size={12} />
            {l.name}
            <ArrowUpRight size={10} style={{ opacity: 0.7 }} />
          </a>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectEditDialog — Founders edit project: name, dates, assignees, drive/swift links
// ---------------------------------------------------------------------------
const ROLE_TABS = [
  { id: 'monteur', label: 'Monteur', color: '#5BBEFC', hint: 'Chaque nouvelle vidéo va à tous les monteurs sélectionnés.',
    filter: (u) => u.role === 'contributor' || u.is_monteur === true },
  { id: 'posteur', label: 'Posteur', color: '#22D3B6', hint: 'Chaque vidéo « À poster » va à tous les posteurs sélectionnés.',
    filter: (u) => u.role !== 'client' },
  { id: 'superviseur', label: 'Superviseur', color: '#F59E0B', hint: 'Ils gèrent le projet, voient les tâches des monteurs et vérifient les vidéos finies.',
    filter: (u) => u.role !== 'client' && u.role !== 'founder' },
];

function RoleAssignPicker({ users, roleTab, setRoleTab, values, setters }) {
  const tab = ROLE_TABS.find((r) => r.id === roleTab);
  const sel = values[roleTab];
  const toggle = (id) => setters[roleTab]((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div data-testid="role-assign-picker">
      <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Membres assignés</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {ROLE_TABS.map((r) => {
          const on = r.id === roleTab;
          return (
            <button key={r.id} type="button" data-testid={`role-tab-${r.id}`} onClick={() => setRoleTab(r.id)} style={{
              flex: 1, padding: '9px 10px', borderRadius: 10, cursor: 'pointer', fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
              background: on ? `${r.color}22` : 'var(--sf2)', border: `1.5px solid ${on ? r.color : 'var(--bd)'}`, color: on ? r.color : 'var(--t2)',
              transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
            }}>{r.label}{values[r.id].length ? ` · ${values[r.id].length}` : ''}</button>
          );
        })}
      </div>
      <div data-testid={`role-members-${roleTab}`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {users.filter((u) => !u.is_archived && tab.filter(u)).map((u) => {
          const on = sel.includes(u.id);
          return (
            <button key={u.id} type="button" data-testid={`role-${roleTab}-${u.id}`} onClick={() => toggle(u.id)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 999, cursor: 'pointer',
              background: on ? `${tab.color}1F` : 'var(--sf2)', border: `1.5px solid ${on ? tab.color : 'var(--bd)'}`,
              color: on ? tab.color : 'var(--t2)', fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
            }}>
              <Avatar user={u} size={18} />{u.name}{on && <Check size={11} />}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 6 }}>{tab.hint}</div>
    </div>
  );
}

function ProjectEditDialog({ project, users, onClose, onSaved }) {
  // Normalize drive_links/swift_links into editable arrays (always at least 1 row)
  const normLinks = (legacy, multi) => {
    const base = [];
    if (Array.isArray(multi) && multi.length) {
      multi.forEach((l) => base.push({ name: l.name || '', url: l.url || '' }));
    } else if (legacy) {
      base.push({ name: legacy.includes('drive') ? 'Drive' : 'Lien', url: legacy });
    }
    if (base.length === 0) base.push({ name: 'Lien 1', url: '' });
    return base;
  };
  const [form, setForm] = useState({
    name: project.name || '',
    client: project.client || '',
    description: project.description || '',
    start_date: project.start_date ? project.start_date.slice(0, 10) : '',
    end_date: project.end_date ? project.end_date.slice(0, 10) : '',
    external_link: project.external_link || '',
  });
  const [driveLinks, setDriveLinks] = useState(normLinks(project.drive_link, project.drive_links));
  const [swiftLinks, setSwiftLinks] = useState(normLinks(project.swift_link, project.swift_links));
  const [assignees] = useState(project.assignees || []);
  const initIds = (many, one) => (many?.length ? many : (one ? [one] : []));
  const [monteurs, setMonteurs] = useState(initIds(project.monteur_ids, project.monteur_id));
  const [derusheurId, setDerusheurId] = useState(project.derusheur_id || '');
  const [posters, setPosters] = useState(initIds(project.poster_ids, project.poster_id));
  const [supervisors, setSupervisors] = useState(project.supervisors || []);
  const [roleTab, setRoleTab] = useState('monteur');
  const [busy, setBusy] = useState(false);

  const allUsers = users || [];

  const updateLink = (setter, idx, patch) =>
    setter((arr) => arr.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLink = (setter, label) =>
    setter((arr) => [...arr, { name: `${label} ${arr.length + 1}`, url: '' }]);
  const removeLink = (setter, idx) =>
    setter((arr) => arr.length <= 1 ? arr : arr.filter((_, i) => i !== idx));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    if (derusheurId && monteurs.includes(derusheurId)) {
      toast.error('Monteur et dérusheur doivent être 2 personnes différentes');
      return;
    }
    setBusy(true);
    try {
      const cleanDrive = driveLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'Drive', url: l.url.trim() }));
      const cleanSwift = swiftLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'SwissTransfer', url: l.url.trim() }));
      // Keep assignees aligned with monteur+derusheur for visibility rules
      const oldRoles = new Set([...initIds(project.monteur_ids, project.monteur_id), ...initIds(project.poster_ids, project.poster_id)]);
      const mergedAssignees = Array.from(new Set([
        ...monteurs, ...posters,
        ...(derusheurId ? [derusheurId] : []),
        ...assignees.filter((x) => !oldRoles.has(x)),
      ]));
      await api.patch(`/projects/${project.id}`, {
        name: form.name.trim(),
        client: form.client.trim(),
        description: form.description,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        external_link: form.external_link.trim() || null,
        drive_links: cleanDrive,
        swift_links: cleanSwift,
        drive_link: null,
        swift_link: null,
        assignees: mergedAssignees,
        monteur_ids: monteurs,
        poster_ids: posters,
        monteur_id: monteurs[0] || null,
        derusheur_id: derusheurId || null,
        poster_id: posters[0] || null,
        supervisors,
      });
      toast.success('Projet mis à jour');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Modifier le projet" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Nom du projet">
          <input
            data-testid="edit-project-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.92rem' }}
          />
        </Field>
        <Field label="Client / studio">
          <input
            value={form.client}
            onChange={(e) => setForm({ ...form, client: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.92rem' }}
          />
        </Field>

        {/* Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Date de début">
            <input
              type="date"
              data-testid="edit-project-start"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
            />
          </Field>
          <Field label="Date de fin">
            <input
              type="date"
              data-testid="edit-project-end"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
            />
          </Field>
        </div>

        <RoleAssignPicker
          users={allUsers}
          roleTab={roleTab} setRoleTab={setRoleTab}
          values={{ monteur: monteurs, posteur: posters, superviseur: supervisors }}
          setters={{ monteur: setMonteurs, posteur: setPosters, superviseur: setSupervisors }}
        />

        <Field label="Dérusheur (Phase 1, optionnel)">
          <select
            data-testid="edit-derusheur"
            value={derusheurId}
            onChange={(e) => setDerusheurId(e.target.value)}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: 13 }}
          >
            <option value="">— Aucun —</option>
            {allUsers.filter((u) => u.role === 'contributor' || u.is_monteur === true).map((u) => (
              <option key={u.id} value={u.id} disabled={monteurs.includes(u.id)}>{u.name}</option>
            ))}
          </select>
        </Field>

        {/* Drive links */}
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>
            <Folder size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Liens Drive (apparaissent tout en haut du projet)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {driveLinks.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6 }}>
                <input
                  data-testid={`edit-drive-name-${i}`}
                  placeholder={`Nom (Drive ${i + 1})`}
                  value={l.name}
                  onChange={(e) => updateLink(setDriveLinks, i, { name: e.target.value })}
                  style={{
                    flex: '0 0 38%', padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                <input
                  data-testid={`edit-drive-url-${i}`}
                  placeholder="https://drive.google.com/…"
                  value={l.url}
                  onChange={(e) => updateLink(setDriveLinks, i, { url: e.target.value })}
                  style={{
                    flex: 1, padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                {driveLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLink(setDriveLinks, i)}
                    style={{
                      padding: '0 10px', borderRadius: 9,
                      background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
                    }}
                  ><Trash2 size={13} color="var(--r)" /></button>
                )}
              </div>
            ))}
            <button
              type="button"
              data-testid="edit-add-drive"
              onClick={() => addLink(setDriveLinks, 'Drive')}
              style={{
                padding: '7px 11px', borderRadius: 9, alignSelf: 'flex-start',
                background: 'rgba(46,163,242,0.10)',
                border: '1.5px dashed rgba(46,163,242,0.4)',
                color: 'var(--pl)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            ><Plus size={12} /> Ajouter un lien Drive</button>
          </div>
        </div>

        {/* SwissTransfer links */}
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>
            <LinkIcon size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Liens SwissTransfer / WeTransfer
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {swiftLinks.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6 }}>
                <input
                  data-testid={`edit-swift-name-${i}`}
                  placeholder={`Nom (Lot ${i + 1})`}
                  value={l.name}
                  onChange={(e) => updateLink(setSwiftLinks, i, { name: e.target.value })}
                  style={{
                    flex: '0 0 38%', padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                <input
                  data-testid={`edit-swift-url-${i}`}
                  placeholder="https://swisstransfer.com/…"
                  value={l.url}
                  onChange={(e) => updateLink(setSwiftLinks, i, { url: e.target.value })}
                  style={{
                    flex: 1, padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                {swiftLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLink(setSwiftLinks, i)}
                    style={{
                      padding: '0 10px', borderRadius: 9,
                      background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
                    }}
                  ><Trash2 size={13} color="var(--r)" /></button>
                )}
              </div>
            ))}
            <button
              type="button"
              data-testid="edit-add-swift"
              onClick={() => addLink(setSwiftLinks, 'Lot')}
              style={{
                padding: '7px 11px', borderRadius: 9, alignSelf: 'flex-start',
                background: 'rgba(167,139,250,0.10)',
                border: '1.5px dashed rgba(167,139,250,0.4)',
                color: '#A78BFA', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            ><Plus size={12} /> Ajouter un lien SwissTransfer</button>
          </div>
        </div>

        <Field label="Description (optionnel)">
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'Lexend', fontSize: '0.88rem' }}
          />
        </Field>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button
            type="submit"
            data-testid="edit-project-submit"
            disabled={busy}
            className="asa-btn"
            style={{ flex: 1 }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : 'Enregistrer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}



function SubTaskRow({ task, users, onChanged, onEdit }) {
  const s = STAGES[task.stage] || STAGES.todo;
  const assignee = users.find((u) => u.id === task.assigned_to);
  const toggleDone = async (e) => {
    e?.stopPropagation?.();
    const next = task.stage === 'done' ? 'todo' : 'done';
    const dx = await stageDeliveryExtra(task, next, CAN_MANAGE.current);
    if (!dx) return;
    try { await api.patch(`/tasks/${task.id}`, { stage: next, ...dx }); onChanged(); }
    catch { toast.error('Erreur'); }
  };
  const openEdit = () => onEdit && onEdit(task);
  return (
    <div
      onClick={openEdit}
      data-testid={`subtask-${task.id}`}
      role={onEdit ? 'button' : undefined}
      tabIndex={onEdit ? 0 : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 8,
        background: 'var(--sf2)', border: '1px solid var(--bd)',
        cursor: onEdit ? 'pointer' : 'default',
        transition: 'border-color 0.15s, background 0.15s',
      }}
      onMouseEnter={(e) => { if (onEdit) e.currentTarget.style.borderColor = 'var(--bdh)'; }}
      onMouseLeave={(e) => { if (onEdit) e.currentTarget.style.borderColor = 'var(--bd)'; }}
    >
      <button onClick={toggleDone} style={{
        width: 16, height: 16, borderRadius: 4,
        background: task.stage === 'done' ? s.color : 'transparent',
        border: `1.5px solid ${s.color}`,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        {task.stage === 'done' && <Check size={10} color="#fff" strokeWidth={3} />}
      </button>
      <span style={{
        flex: 1, fontFamily: 'Lexend', fontSize: '0.82rem',
        color: task.stage === 'done' ? 'var(--t3)' : 'var(--t1)',
        textDecoration: task.stage === 'done' ? 'line-through' : 'none',
      }}>{task.title}</span>
      {assignee && <Avatar user={assignee} size={20} />}
      {onEdit && (
        <ArrowUpRight size={12} color="var(--t3)" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Project create dialog
// ---------------------------------------------------------------------------
function ProjectDialog({ onClose, onSaved, folderId, users = [], initialValues = null, pendingTasks = null, modalTitle = 'Nouveau projet', submitLabel = 'Créer' }) {
  const [form, setForm] = useState({
    name: initialValues?.name || '',
    client: initialValues?.client || '',
    icon: initialValues?.icon || 'camera',
    color: initialValues?.color || COLOR_CHOICES[0],
    description: initialValues?.description || '',
    tags: initialValues?.tags || '',
    start_date: initialValues?.start_date || '',
    end_date: initialValues?.end_date || '',
    video_count: initialValues?.video_count || (pendingTasks ? String(pendingTasks.length) : ''),
    videos_per_day: '1', // Iter 62ea — cadence quotidienne, N vidéos auto-créées à la création
  });
  const [driveLinks, setDriveLinks] = useState(
    initialValues?.drive_links?.length ? initialValues.drive_links : [{ name: 'Lien 1', url: '' }]
  );
  const [swiftLinks, setSwiftLinks] = useState(
    initialValues?.swift_links?.length ? initialValues.swift_links : [{ name: 'Lien 1', url: '' }]
  );
  const [assignees, setAssignees] = useState(initialValues?.assignees || []);
  const [busy, setBusy] = useState(false);

  // Everyone can be assigned (founders + monteurs) — user explicitly asked for "Hugo, Rayan, …"
  const allUsers = users;
  const toggleAssignee = (id) =>
    setAssignees((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const updateLink = (setter, idx, patch) =>
    setter((arr) => arr.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLink = (setter, label) =>
    setter((arr) => [...arr, { name: `${label} ${arr.length + 1}`, url: '' }]);
  const removeLink = (setter, idx) =>
    setter((arr) => arr.length <= 1 ? arr : arr.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    // Dates are now OPTIONAL — only validate consistency if both are filled
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error('La deadline doit être après la date de début');
      return;
    }
    setBusy(true);
    try {
      const tags = (typeof form.tags === 'string' ? form.tags.split(',') : (form.tags || [])).map((t) => t.trim()).filter(Boolean);
      const cleanDrive = driveLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'Drive', url: l.url.trim() }));
      const cleanSwift = swiftLinks.filter((l) => l.url.trim()).map((l) => ({ name: l.name.trim() || 'SwissTransfer', url: l.url.trim() }));
      const pr = await api.post('/projects', {
        ...form,
        tags,
        folder: folderId || initialValues?.folder || 'internal',
        drive_links: cleanDrive,
        swift_links: cleanSwift,
        assignees,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        video_count: form.video_count ? parseInt(form.video_count, 10) : null,
        videos_per_day: Math.max(1, Math.min(parseInt(form.videos_per_day || '1', 10) || 1, 20)),
      });
      // If parent provided pendingTasks (e.g. FromDocDialog), create them now
      if (pendingTasks && pendingTasks.length > 0) {
        const projectId = pr.data.id;
        const driveFallback = cleanDrive[0]?.url || '';
        for (let i = 0; i < pendingTasks.length; i++) {
          const t = pendingTasks[i];
          let conceptId = null;
          try {
            const c = await api.post('/concepts', {
              title: t.title,
              format: t.format || 'special',
              description: t.description || '',
              inspiration_link: t.inspiration_link || null,
              project_id: projectId,
            });
            conceptId = c.data.id;
          } catch { /* skip */ }
          // Iter47w — Task title = "Concept N — Format — Titre du concept" (was "Jour N — …")
          const formattedTitle = `Concept ${i + 1} — ${formatLabelLight(t.format)} — ${t.title}`;
          await api.post('/tasks', {
            title: formattedTitle,
            description: t.description || '',
            project_id: projectId,
            assigned_to: t.assigned_to || null,
            concept_id: conceptId,
            tech_instructions: t.tech_instructions || driveFallback,
            inspiration_link: t.inspiration_link || '',
            stage: 'todo',
            priority: 'medium',
            is_macro: true,
            kind: 'macro',
            order: i,
          });
        }
        toast.success(`Projet créé avec ${pendingTasks.length} tâche${pendingTasks.length > 1 ? 's' : ''}`);
      } else {
        toast.success('Projet créé');
      }
      onSaved(pr.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={modalTitle} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* === STEP 1 — À QUI EST DESTINÉ CE PROJET ? === */}
        {allUsers.length > 0 && (
          <div
            style={{
              padding: 14, borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(46,163,242,0.06), rgba(167,139,250,0.04))',
              border: '1.5px solid rgba(46,163,242,0.25)',
            }}
          >
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
              color: 'var(--pl)', letterSpacing: '0.08em', textTransform: 'uppercase',
              marginBottom: 4,
            }}>
              Étape 1 · À qui est destiné ce projet ?
            </div>
            <div style={{
              fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginBottom: 10,
            }}>
              Sélectionne les personnes qui auront accès au projet.
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {allUsers.map((u) => {
                const active = assignees.includes(u.id);
                return (
                  <button
                    key={u.id} type="button"
                    data-testid={`project-assignee-${u.id}`}
                    onClick={() => toggleAssignee(u.id)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '7px 12px', borderRadius: 999,
                      background: active ? `${u.color}26` : 'var(--sf2)',
                      border: `1.5px solid ${active ? u.color : 'var(--bd)'}`,
                      color: active ? u.color : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                      transition: 'all 0.15s',
                    }}
                  >
                    <Avatar user={u} size={20} />
                    {u.name}
                    {active && <Check size={12} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* === STEP 2 — INFOS DE BASE === */}
        <Field label="Nom du projet">
          <input
            data-testid="project-name-input"
            autoFocus
            placeholder="Ex: Campagne Nordic – Avril" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.92rem' }}
          />
        </Field>
        <Field label="Client / studio">
          <input
            placeholder="Ex: Studio Nordic" value={form.client}
            onChange={(e) => setForm({ ...form, client: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.92rem' }}
          />
        </Field>

        {/* === Dates (optionnelles) === */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Date de début (optionnel)">
            <input
              type="date"
              data-testid="project-start-date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
            />
          </Field>
          <Field label="Date de fin (optionnel)">
            <input
              type="date"
              data-testid="project-end-date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
            />
          </Field>
        </div>

        {/* Feasibility hint */}
        {(() => {
          if (!form.start_date || !form.end_date || form.end_date < form.start_date) return null;
          const days = Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000) + 1);
          const videos = parseInt(form.video_count || '0', 10) || 0;
          if (!videos) return (
            <div style={{
              padding: '10px 12px', borderRadius: 10, marginTop: 8,
              background: 'rgba(46,163,242,0.07)', border: '1px dashed rgba(46,163,242,0.3)',
              fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)',
            }}>
              📅 {days} jour{days > 1 ? 's' : ''} disponibles. Indique le <b>nombre de vidéos</b> ci-dessous pour estimer la faisabilité.
            </div>
          );
          const perDay = videos / days;
          const ok = perDay <= 1.0;
          return (
            <div style={{
              padding: '11px 13px', borderRadius: 10, marginTop: 8,
              background: ok ? 'rgba(52,211,153,0.08)' : 'rgba(245,166,35,0.10)',
              border: `1px solid ${ok ? 'rgba(52,211,153,0.4)' : 'rgba(245,166,35,0.45)'}`,
              fontFamily: 'Lexend', fontSize: 12, color: 'var(--t1)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {ok ? <Check size={13} color="#34D399" /> : <AlertTriangle size={13} color="#F5A623" />}
              <div style={{ flex: 1 }}>
                <b style={{ color: ok ? '#34D399' : '#F5A623' }}>
                  {ok ? 'Faisable' : 'Cadence serrée'}
                </b>
                {' — '}{videos} vidéos / {days} jours = <b>{perDay.toFixed(2)} vidéos/jour</b>.
                {!ok && <span style={{ color: 'var(--t3)' }}> Tu peux quand même créer le projet, mais prévois un planning serré.</span>}
              </div>
            </div>
          );
        })()}

        {/* Video count (used for feasibility) */}
        <Field label="Nombre de vidéos prévues (optionnel)">
          <input
            type="number"
            min="0"
            data-testid="project-video-count"
            value={form.video_count || ''}
            onChange={(e) => setForm({ ...form, video_count: e.target.value })}
            placeholder="Ex: 30"
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
          />
        </Field>

        {/* Iter 62ea — Cadence quotidienne : N vidéos auto-créées à la création (statut "À faire") */}
        {!initialValues?.id && (
          <Field label="Cadence : combien de vidéos aujourd'hui ?">
            <input
              type="number"
              min="1"
              max="20"
              data-testid="project-videos-per-day"
              value={form.videos_per_day || '1'}
              onChange={(e) => setForm({ ...form, videos_per_day: e.target.value })}
              placeholder="1"
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
            />
            <div style={{ fontSize: '0.72rem', color: 'var(--t3)', marginTop: 4, fontFamily: 'Lexend' }}>
              Ces vidéos apparaîtront directement dans le Kanban en « À faire ». Tu peux en ajouter d'autres plus tard.
            </div>
          </Field>
        )}

        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Icône</div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {ICON_CHOICES.map((ic) => {
              const Ic = PROJECT_ICONS[ic];
              return (
                <button
                  key={ic} type="button"
                  onClick={() => setForm({ ...form, icon: ic })}
                  style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: form.icon === ic ? `${form.color}22` : 'var(--sf2)',
                    border: `1.5px solid ${form.icon === ic ? form.color : 'var(--bd)'}`,
                    display: 'grid', placeItems: 'center',
                  }}
                >
                  <Ic size={16} color={form.icon === ic ? form.color : 'var(--t3)'} strokeWidth={1.6} />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Couleur</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {COLOR_CHOICES.map((c) => (
              <button
                key={c} type="button"
                onClick={() => setForm({ ...form, color: c })}
                style={{
                  width: 30, height: 30, borderRadius: '50%', background: c,
                  display: 'grid', placeItems: 'center',
                }}
              >
                {form.color === c && <Check size={14} color="#fff" strokeWidth={3} />}
              </button>
            ))}
          </div>
        </div>

        {/* === Drive links (multi) === */}
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>
            <Folder size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Liens Drive (rushes / source)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {driveLinks.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6 }}>
                <input
                  data-testid={`drive-link-name-${i}`}
                  placeholder={`Nom (Drive ${i + 1})`}
                  value={l.name}
                  onChange={(e) => updateLink(setDriveLinks, i, { name: e.target.value })}
                  style={{
                    flex: '0 0 38%', padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                <input
                  data-testid={`drive-link-url-${i}`}
                  placeholder="https://drive.google.com/…"
                  value={l.url}
                  onChange={(e) => updateLink(setDriveLinks, i, { url: e.target.value })}
                  style={{
                    flex: 1, padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                {driveLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLink(setDriveLinks, i)}
                    style={{
                      padding: '0 10px', borderRadius: 9,
                      background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
                    }}
                  ><Trash2 size={13} color="var(--r)" /></button>
                )}
              </div>
            ))}
            <button
              type="button"
              data-testid="add-drive-link"
              onClick={() => addLink(setDriveLinks, 'Drive')}
              style={{
                padding: '7px 11px', borderRadius: 9, alignSelf: 'flex-start',
                background: 'rgba(46,163,242,0.10)',
                border: '1.5px dashed rgba(46,163,242,0.4)',
                color: 'var(--pl)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            ><Plus size={12} /> Ajouter un lien Drive</button>
          </div>
        </div>

        {/* === SwissTransfer / WeTransfer links (multi) === */}
        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>
            <LinkIcon size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Liens SwissTransfer / WeTransfer
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {swiftLinks.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6 }}>
                <input
                  data-testid={`swift-link-name-${i}`}
                  placeholder={`Nom (Lot ${i + 1})`}
                  value={l.name}
                  onChange={(e) => updateLink(setSwiftLinks, i, { name: e.target.value })}
                  style={{
                    flex: '0 0 38%', padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                <input
                  data-testid={`swift-link-url-${i}`}
                  placeholder="https://swisstransfer.com/…"
                  value={l.url}
                  onChange={(e) => updateLink(setSwiftLinks, i, { url: e.target.value })}
                  style={{
                    flex: 1, padding: '8px 10px',
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 9,
                    fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                  }}
                />
                {swiftLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLink(setSwiftLinks, i)}
                    style={{
                      padding: '0 10px', borderRadius: 9,
                      background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)',
                    }}
                  ><Trash2 size={13} color="var(--r)" /></button>
                )}
              </div>
            ))}
            <button
              type="button"
              data-testid="add-swift-link"
              onClick={() => addLink(setSwiftLinks, 'Lot')}
              style={{
                padding: '7px 11px', borderRadius: 9, alignSelf: 'flex-start',
                background: 'rgba(167,139,250,0.10)',
                border: '1.5px dashed rgba(167,139,250,0.4)',
                color: '#A78BFA', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            ><Plus size={12} /> Ajouter un lien SwissTransfer</button>
          </div>
        </div>

        <Field label="Tags (séparés par des virgules)">
          <input
            placeholder="Ex: avancerent, video" value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem' }}
          />
        </Field>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button data-testid="project-submit" type="submit" disabled={busy} className="asa-btn" style={{ flex: 1 }}>
            {busy ? <Loader2 size={15} className="spin" /> : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SubTaskDialog({ projectId, projectAssignees, users, existingCount, onClose, onSaved }) {
  // If a project has only one assignee → auto-select them. Otherwise let user pick.
  const defaultAssignee = (projectAssignees && projectAssignees.length === 1) ? projectAssignees[0] : null;
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium',
    assigned_to: defaultAssignee, due_date: '',
  });
  const [busy, setBusy] = useState(false);

  // Show project assignees first (more relevant), then others
  const sortedUsers = useMemo(() => {
    const set = new Set(projectAssignees || []);
    const inProj = users.filter((u) => set.has(u.id));
    const others = users.filter((u) => !set.has(u.id));
    return [...inProj, ...others];
  }, [users, projectAssignees]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    setBusy(true);
    try {
      // Critical fix: manual project tasks MUST be is_macro=true to appear in the
      // ProjectDetailView flowchart (rootTasks filter requires is_macro). order=
      // existing macro count so the new task lands at the bottom of the list.
      await api.post('/tasks', {
        title: form.title.trim(),
        description: form.description || '',
        priority: form.priority,
        assigned_to: form.assigned_to || null,
        due_date: form.due_date || null,
        project_id: projectId,
        stage: 'todo',
        is_macro: true,
        kind: 'manual',
        order: (existingCount ?? 0) + 1000, // ensures append at end
      });
      toast.success('Tâche ajoutée');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Ajouter une tâche" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Titre">
          <input
            data-testid="subtask-title"
            autoFocus placeholder="Ex: Réception des RUSH" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={{ width: '100%' }}
          />
        </Field>
        <Field label="Description (optionnel)">
          <textarea
            data-testid="subtask-description"
            rows={2} placeholder="Détail court…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'Lexend', fontSize: '0.86rem' }}
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Assigné à">
            <select
              data-testid="subtask-assignee"
              value={form.assigned_to || ''}
              onChange={(e) => setForm({ ...form, assigned_to: e.target.value || null })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem' }}
            >
              <option value="">Non assigné</option>
              {sortedUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{(projectAssignees || []).includes(u.id) ? ' · projet' : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priorité">
            <select
              data-testid="subtask-priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem' }}
            >
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
            </select>
          </Field>
        </div>
        <Field label="Échéance (optionnel)">
          <input
            data-testid="subtask-due-date"
            type="date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.88rem', colorScheme: 'dark' }}
          />
        </Field>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button data-testid="subtask-submit" type="submit" disabled={busy || !form.title.trim()} className="asa-btn" style={{ flex: 1 }}>
            {busy ? <Loader2 size={15} className="spin" /> : 'Ajouter'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EmptyProjects({ onCreate }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{
        width: 72, height: 72, borderRadius: 18,
        background: 'var(--pbg)', border: '1.5px solid var(--pbd)',
        margin: '0 auto 16px', display: 'grid', placeItems: 'center',
      }}>
        <Camera size={26} color="var(--pl)" strokeWidth={1.4} />
      </div>
      <div className="t-display-md" style={{ color: 'var(--t1)' }}>Aucun projet</div>
      <div className="t-body-md" style={{ color: 'var(--t3)', marginTop: 6, marginBottom: 18 }}>
        Crée ton premier projet pour structurer tes campagnes
      </div>
      <button data-testid="project-empty-create" onClick={onCreate} className="asa-btn" style={{ margin: '0 auto' }}>
        <Plus size={15} /> Nouveau projet
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generic Modal + Field
// ---------------------------------------------------------------------------
function Modal({ title, onClose, children }) {
  // Escape key handler
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '24px 18px', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{
        width: '100%', maxWidth: 560,
        background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22, padding: 24,
        margin: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>{title}</h2>
          <button onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="asa-eyebrow" style={{ marginBottom: 7 }}>{label}</div>
      <div style={{
        padding: '11px 14px', background: 'var(--sf2)',
        border: '1.5px solid var(--bd)', borderRadius: 12,
      }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Project — Choice between blank and framework
// ---------------------------------------------------------------------------
const FRAMEWORKS = [
  {
    id: 'montage',
    name: 'Gestion de projet Montage',
    tagline: 'Lot de N vidéos · 8 étapes auto',
    color: '#A78BFA',
    icon: 'video',
    description: '6 tâches macro (Réception → Dérush → Vérif → Création → Montage → Vérification) + 1 sous-tâche par vidéo.',
    available: true,
  },
  {
    id: 'short',
    name: 'Vidéo courte (Reel / TikTok)',
    tagline: 'Bientôt disponible',
    color: '#5BBEFC',
    icon: 'flame',
    description: 'Idéation → Tournage → Montage → Validation → Publication.',
    available: false,
  },
  {
    id: 'newclient',
    name: 'Signer un nouveau client',
    tagline: 'Bientôt disponible',
    color: '#34D399',
    icon: 'briefcase',
    description: 'Onboarding → Brief stratégique → Setup outils → Plan J30 → Livraison test.',
    available: false,
  },
];

// ---------------------------------------------------------------------------
// FolderStepDialog — step 1 of project creation: pick a folder/category
// ---------------------------------------------------------------------------
function FolderStepDialog({ folders, onClose, onPick, onCreated }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#5BBEFC');
  const [creating, setCreating] = useState(false);

  const NEW_FOLDER_COLORS = ['#FF6B6B', '#F5A623', '#FBBF24', '#34D399', '#5BBEFC', '#2EA3F2', '#A78BFA', '#F472B6'];

  const create = async () => {
    if (!newName.trim()) { toast.error('Nom requis'); return; }
    setCreating(true);
    try {
      const r = await api.post('/folders', { name: newName.trim(), color: newColor, icon: 'folder' });
      onCreated(r.data);
      onPick(r.data.id);
    } catch { toast.error('Erreur'); }
    finally { setCreating(false); }
  };

  return (
    <Modal title="Dans quel dossier ?" onClose={onClose}>
      <p style={{
        fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)',
        margin: '0 0 16px',
      }}>Choisis la catégorie de ton projet avant de le créer.</p>

      <div className="asa-eyebrow" style={{ marginBottom: 10, paddingLeft: 4 }}>Dossiers disponibles</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
        {folders.map((f) => (
          <button
            key={f.id}
            data-testid={`folder-pick-${f.id}`}
            onClick={() => onPick(f.id)}
            className="asa-card row-card"
            style={{
              padding: 12, width: '100%', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 12,
              transition: 'border-color 0.18s var(--ease-out)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${f.color}66`)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--bd)')}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: `${f.color}1A`, border: `1.5px solid ${f.color}44`,
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Folder size={15} color={f.color} strokeWidth={1.7} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5,
                color: 'var(--t1)',
              }}>{f.name}</div>
              {!f.is_default && (
                <div style={{
                  fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 2,
                }}>Dossier personnalisé</div>
              )}
            </div>
            <ChevronRight size={14} color="var(--t3)" />
          </button>
        ))}
      </div>

      {/* Create a new folder inline */}
      <div className="asa-eyebrow" style={{ marginBottom: 10, paddingLeft: 4 }}>Créer un nouveau dossier</div>
      <div style={{
        padding: 12, borderRadius: 12,
        background: 'var(--sf2)', border: '1.5px solid var(--bd)',
      }}>
        <div className="asa-field" style={{ marginBottom: 10 }}>
          <input
            data-testid="new-folder-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Ex. Tournages, Brand, Live…"
            style={{ flex: 1, fontSize: 13.5 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {NEW_FOLDER_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setNewColor(c)}
              data-testid={`new-folder-color-${c.slice(1)}`}
              style={{
                width: 22, height: 22, borderRadius: 7,
                background: c,
                border: newColor === c ? '2.5px solid #fff' : '2.5px solid transparent',
                boxShadow: newColor === c ? `0 0 0 2px ${c}88` : 'none',
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
        <button
          onClick={create}
          disabled={creating || !newName.trim()}
          data-testid="new-folder-create"
          className="asa-btn"
          style={{ width: '100%', opacity: (creating || !newName.trim()) ? 0.5 : 1 }}
        >
          <Plus size={14} /> {creating ? 'Création…' : 'Créer & continuer'}
        </button>
      </div>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Project type choice (Vide / Framework)
// ---------------------------------------------------------------------------
function NewProjectChoice({ onClose, onBack, onPickBlank, onPickFramework, onPickFromDoc, folder }) {
  return (
    <Modal title="Nouveau projet" onClose={onClose}>
      {/* Selected folder pill (shows where the new project will go) */}
      {folder && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 16, padding: '8px 12px', borderRadius: 10,
          background: `${folder.color}1A`, border: `1.5px solid ${folder.color}44`,
        }}>
          <div style={{
            width: 26, height: 26, borderRadius: 7,
            background: `${folder.color}33`, border: `1.5px solid ${folder.color}66`,
            display: 'grid', placeItems: 'center', flexShrink: 0,
          }}>
            <Folder size={12} color={folder.color} strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 10,
              color: folder.color, textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>Catégorie sélectionnée</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
              color: 'var(--t1)',
            }}>{folder.name}</div>
          </div>
          {onBack && (
            <button
              data-testid="choice-back"
              onClick={onBack}
              style={{
                padding: '5px 10px', borderRadius: 8,
                background: 'transparent', border: '1px solid var(--bd)',
                color: 'var(--t3)', fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
              }}
            >Changer</button>
          )}
        </div>
      )}

      {/* Quick blank create */}
      <button
        data-testid="choice-blank"
        onClick={onPickBlank}
        className="asa-card row-card"
        style={{
          padding: 16, width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10,
        }}
      >
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: 'var(--pbg)', border: '1.5px solid var(--pbd)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Plus size={20} color="var(--pl)" strokeWidth={1.7} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.94rem', color: 'var(--t1)' }}>
            Créer un projet vide
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.76rem', color: 'var(--t3)', marginTop: 3 }}>
            Tu rempliras chaque étape toi-même
          </div>
        </div>
        <ChevronRight size={16} color="var(--t3)" />
      </button>

      {/* Auto-generate from a pasted document */}
      <button
        data-testid="choice-from-doc"
        onClick={onPickFromDoc}
        className="asa-card row-card"
        style={{
          padding: 16, width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16,
        }}
      >
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: 'rgba(167,139,250,0.16)',
          border: '1.5px solid rgba(167,139,250,0.4)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Sparkles size={20} color="#C4B5FD" strokeWidth={1.7} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.94rem', color: 'var(--t1)' }}>
              Générer depuis un document
            </div>
            <span style={{
              padding: '1px 7px', borderRadius: 999,
              background: 'rgba(167,139,250,0.18)', color: '#C4B5FD',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>IA</span>
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.76rem', color: 'var(--t3)', marginTop: 3 }}>
            Colle un brief, Claude extrait le projet et les tâches
          </div>
        </div>
        <ChevronRight size={16} color="var(--t3)" />
      </button>

      <div className="asa-eyebrow" style={{ marginBottom: 10, paddingLeft: 4 }}>
        Frameworks · Process pré-définis
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {FRAMEWORKS.map((fw) => {
          const Ic = PROJECT_ICONS[fw.icon] || Camera;
          return (
            <button
              key={fw.id}
              data-testid={`choice-fw-${fw.id}`}
              onClick={() => fw.available && onPickFramework(fw.id)}
              disabled={!fw.available}
              className="asa-card"
              style={{
                padding: 14, textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 12,
                opacity: fw.available ? 1 : 0.5,
                cursor: fw.available ? 'pointer' : 'not-allowed',
                transition: 'border-color 0.2s var(--ease-out)',
              }}
              onMouseEnter={(e) => fw.available && (e.currentTarget.style.borderColor = `${fw.color}55`)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--bd)')}
            >
              <div style={{
                width: 38, height: 38, borderRadius: 11,
                background: `${fw.color}1A`, border: `1.5px solid ${fw.color}44`,
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <Ic size={16} color={fw.color} strokeWidth={1.6} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.86rem', color: 'var(--t1)' }}>
                    {fw.name}
                  </span>
                  {!fw.available && (
                    <span style={{
                      fontFamily: 'Sora', fontWeight: 600, fontSize: '0.58rem',
                      color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em',
                      padding: '2px 7px', borderRadius: 999, background: 'var(--sf2)',
                    }}>Bientôt</span>
                  )}
                </div>
                <div style={{ fontFamily: 'Lexend', fontSize: '0.74rem', color: 'var(--t3)', marginTop: 3, lineHeight: 1.4 }}>
                  {fw.description}
                </div>
              </div>
              {fw.available && <ChevronRight size={15} color="var(--t3)" style={{ flexShrink: 0 }} />}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Montage framework — mini questionnaire
// ---------------------------------------------------------------------------
const FORMAT_OPTIONS = ['Podcast', 'Face cam', 'Technique', 'Spécial', 'Autres'];

function MontageFrameworkDialog({ users, onClose, onSaved, folderId }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    name: '', client: '', client_id: null, client_color: '#A78BFA',
    video_count: 15,
    formats: ['Podcast'],
    rush_date: '', first_publish_date: '',
    members: users.map((u) => u.id),
    external_link: '',
    client_drive_link: '',
    concepts_link: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/clients').then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const pickClient = (c) => setForm((s) => ({
    ...s, client: c.name, client_id: c.id, client_color: c.color,
  }));
  const createClient = async () => {
    const name = (form.client || '').trim();
    if (!name) return;
    try {
      const r = await api.post('/clients', { name, color: form.client_color });
      setClients((cs) => [...cs.filter((c) => c.id !== r.data.id), r.data]);
      setForm((s) => ({ ...s, client_id: r.data.id, client: r.data.name, client_color: r.data.color }));
    } catch { toast.error('Erreur création client'); }
  };

  const toggleFormat = (f) => {
    setForm((s) => ({
      ...s,
      formats: s.formats.includes(f) ? s.formats.filter((x) => x !== f) : [...s.formats, f],
    }));
  };
  const toggleMember = (id) => {
    setForm((s) => ({
      ...s,
      members: s.members.includes(id) ? s.members.filter((x) => x !== id) : [...s.members, id],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Nom du projet requis'); return; }
    if (form.video_count < 1) { toast.error('Au moins 1 vidéo'); return; }
    setBusy(true);
    try {
      // Ensure client exists (create on the fly if user typed a new name)
      let clientId = form.client_id;
      if (!clientId && form.client.trim()) {
        const cr = await api.post('/clients', { name: form.client.trim(), color: form.client_color });
        clientId = cr.data.id;
      }
      const r = await api.post('/projects/from-framework', {
        framework: 'montage',
        name: form.name.trim(),
        client: form.client.trim(),
        client_id: clientId,
        video_count: Number(form.video_count),
        formats: form.formats,
        rush_date: form.rush_date || null,
        first_publish_date: form.first_publish_date || null,
        members: form.members,
        external_link: form.external_link.trim() || null,
        client_drive_link: form.client_drive_link.trim() || null,
        concepts_link: form.concepts_link.trim() || null,
        folder: folderId || 'youtube',
      });
      toast.success(`Projet lancé · ${r.data.tasks_created} tâches générées`);
      onSaved(r.data.project);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Lancer un Montage" onClose={onClose}>
      {/* Framework badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '5px 11px', borderRadius: 999,
        background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.35)',
        marginBottom: 18,
      }}>
        <Video size={11} color="#A78BFA" />
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.66rem', color: '#A78BFA', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Framework · Montage
        </span>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Nom du projet">
          <input
            data-testid="fw-name"
            autoFocus placeholder="Ex: Podcast Hakim Mai"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.92rem' }}
          />
        </Field>

        <Field label="Client">
          <ClientPicker
            clients={clients}
            value={form.client}
            color={form.client_color}
            onPickExisting={pickClient}
            onChangeName={(name) => setForm({ ...form, client: name, client_id: null })}
            onChangeColor={(color) => setForm({ ...form, client_color: color })}
            onCreate={createClient}
          />
        </Field>

        <Field label="Nombre de vidéos à produire">
          <input
            data-testid="fw-count"
            type="number" min={1} max={100}
            value={form.video_count}
            onChange={(e) => setForm({ ...form, video_count: e.target.value })}
            style={{ width: '100%', fontFamily: 'Sora', fontWeight: 700, fontSize: '1rem' }}
          />
        </Field>

        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Formats</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {FORMAT_OPTIONS.map((f) => {
              const active = form.formats.includes(f);
              return (
                <button
                  key={f} type="button"
                  onClick={() => toggleFormat(f)}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    background: active ? 'rgba(167,139,250,0.16)' : 'var(--sf2)',
                    border: `1.5px solid ${active ? 'rgba(167,139,250,0.55)' : 'var(--bd)'}`,
                    color: active ? '#C4B5FD' : 'var(--t3)',
                    fontFamily: 'Sora', fontWeight: 600, fontSize: '0.74rem',
                  }}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Réception rushs">
            <input
              data-testid="fw-rush-date"
              type="date" value={form.rush_date}
              onChange={(e) => setForm({ ...form, rush_date: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.86rem', colorScheme: 'dark' }}
            />
          </Field>
          <Field label="1ère publication">
            <input
              data-testid="fw-pub-date"
              type="date" value={form.first_publish_date}
              onChange={(e) => setForm({ ...form, first_publish_date: e.target.value })}
              style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.86rem', colorScheme: 'dark' }}
            />
          </Field>
        </div>

        <div>
          <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Groupe ASA · membres</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {users.map((u) => {
              const active = form.members.includes(u.id);
              return (
                <button
                  key={u.id} type="button"
                  onClick={() => toggleMember(u.id)}
                  data-testid={`fw-member-${u.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '5px 11px 5px 5px', borderRadius: 999,
                    background: active ? 'rgba(46,163,242,0.10)' : 'var(--sf2)',
                    border: `1.5px solid ${active ? 'rgba(46,163,242,0.4)' : 'var(--bd)'}`,
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${u.color}, ${u.color}CC)`,
                    display: 'grid', placeItems: 'center',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: '0.62rem', color: '#fff',
                  }}>{(u.short || u.name?.[0] || '?').toUpperCase()}</div>
                  <span style={{ fontFamily: 'Lexend', fontSize: '0.78rem', color: active ? 'var(--t1)' : 'var(--t3)' }}>
                    {u.name.split(' ')[0]}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.7rem', color: 'var(--t3)', marginTop: 7 }}>
            Une conversation de groupe sera créée avec ces membres.
          </div>
        </div>

        <Field label="Lien des rushs (Drive / Swiss Transfer) — optionnel">
          <input
            data-testid="fw-link"
            placeholder="https://drive.google.com/…"
            value={form.external_link}
            onChange={(e) => setForm({ ...form, external_link: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.84rem' }}
          />
        </Field>

        <Field label="Drive du client — optionnel">
          <input
            data-testid="fw-client-drive"
            placeholder="https://drive.google.com/…"
            value={form.client_drive_link}
            onChange={(e) => setForm({ ...form, client_drive_link: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.84rem' }}
          />
        </Field>

        <Field label="Drive concepts / inspirations — optionnel">
          <input
            data-testid="fw-concepts"
            placeholder="https://drive.google.com/…"
            value={form.concepts_link}
            onChange={(e) => setForm({ ...form, concepts_link: e.target.value })}
            style={{ width: '100%', fontFamily: 'Lexend', fontSize: '0.84rem' }}
          />
        </Field>

        {/* Recap */}
        <div style={{
          padding: 12, borderRadius: 12,
          background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)',
        }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.72rem', color: '#C4B5FD', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Ce qui sera créé
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontFamily: 'Lexend', fontSize: '0.78rem', color: 'var(--t2)', lineHeight: 1.6 }}>
            <li>1 projet «&nbsp;{form.name || 'Nouveau lot'}&nbsp;»</li>
            <li>8 tâches macro (réception → publication)</li>
            <li>{form.video_count || 0} sous-tâche{form.video_count > 1 ? 's' : ''} «&nbsp;Vidéo X&nbsp;»</li>
            <li>1 conversation de groupe ({form.members.length} membres)</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>
            Annuler
          </button>
          <button
            data-testid="fw-submit"
            type="submit"
            disabled={busy || !form.name.trim()}
            className="asa-btn"
            style={{ flex: 1.4, background: '#A78BFA' }}
          >
            {busy ? <Loader2 size={15} className="spin" /> : 'Lancer le montage'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


// ===========================================================================
// InspirationsPanel — collapsible block of project inspirations (links / refs)
// ===========================================================================
function InspirationsPanel({ project, users, onAdd, onRemoved }) {
  const [open, setOpen] = useState(true);
  const items = project.inspirations || [];

  const remove = async (id) => {
    try { await api.delete(`/projects/${project.id}/inspirations/${id}`); onRemoved(); }
    catch { toast.error('Erreur'); }
  };

  return (
    <div className="asa-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(!open); }}
        data-testid="inspirations-toggle"
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', padding: '12px 16px',
          background: 'transparent', textAlign: 'left', cursor: 'pointer',
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: 'rgba(245,166,35,0.13)',
          border: '1px solid rgba(245,166,35,0.3)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Lightbulb size={14} color="#F5A623" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.92rem', color: 'var(--t1)' }}>
            Inspirations
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: '0.72rem', color: 'var(--t3)' }}>
            {items.length === 0 ? 'Ajoute des références visuelles ou textuelles' : `${items.length} référence${items.length > 1 ? 's' : ''}`}
          </div>
        </div>
        <button
          data-testid="add-inspiration-btn"
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 999,
            background: 'rgba(245,166,35,0.13)', color: '#F5A623',
            border: '1px solid rgba(245,166,35,0.3)',
            fontFamily: 'Sora', fontWeight: 600, fontSize: '0.72rem',
            cursor: 'pointer',
          }}
        >
          <Plus size={12} /> Ajouter
        </button>
        <ChevronDown
          size={15} color="var(--t3)"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}
        />
      </div>

      {open && items.length > 0 && (
        <div style={{
          padding: '4px 14px 14px',
          borderTop: '1px solid var(--bd)',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8,
        }}>
          {items.map((it) => (
            <InspirationCard key={it.id} item={it} onRemove={() => remove(it.id)} users={users} />
          ))}
        </div>
      )}
    </div>
  );
}

function InspirationCard({ item, onRemove }) {
  let host = '';
  try { host = new URL(item.url).hostname.replace('www.', ''); } catch { host = item.url; }
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '9px 12px',
        background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 10,
        textDecoration: 'none', position: 'relative',
      }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: 7,
        background: 'rgba(46,163,242,0.10)',
        border: '1px solid rgba(46,163,242,0.2)',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <LinkIcon size={12} color="var(--pl)" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem', color: 'var(--t1)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{item.title || host}</div>
        <div style={{
          fontFamily: 'Lexend', fontSize: '0.66rem', color: 'var(--t3)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{host}</div>
      </div>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
        title="Retirer"
        style={{ padding: 4, color: 'var(--t3)' }}
      >
        <X size={12} />
      </button>
    </a>
  );
}

// ===========================================================================
// InspirationModal — quick add dialog
// ===========================================================================
function InspirationModal({ project, onClose, onSaved }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/inspirations`, {
        url: url.trim(),
        title: title.trim() || null,
      });
      toast.success('Inspiration ajoutée');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(6,11,24,0.7)', backdropFilter: 'blur(8px)',
      display: 'grid', placeItems: 'center', padding: 18,
    }}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 440,
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 20, padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)' }}>Nouvelle inspiration</h2>
          <button type="button" onClick={onClose} style={{ marginLeft: 'auto', padding: 6 }}>
            <X size={18} color="var(--t3)" />
          </button>
        </div>
        <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Lien</div>
        <input
          autoFocus
          data-testid="insp-url"
          type="url"
          placeholder="https://…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          style={projInputStyle}
        />
        <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Titre (optionnel)</div>
        <input
          data-testid="insp-title"
          placeholder="Ex: Référence montage hook"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={projInputStyle}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button data-testid="insp-submit" type="submit" disabled={busy} className="asa-btn" style={{ flex: 1.3 }}>
            {busy ? <Loader2 size={15} className="spin" /> : 'Ajouter'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ===========================================================================
// FichesEditor — inline editor for the optional "Fiche de poste" macro
// One block per video : cover URL + title + description
// ===========================================================================
function FichesEditor({ project, onChanged }) {
  const initial = useMemo(() => {
    const list = project.fiches || [];
    const count = project.video_count || list.length || 0;
    return Array.from({ length: count }).map((_, i) => ({
      video_idx: i,
      cover_url: list[i]?.cover_url || '',
      title: list[i]?.title || '',
      description: list[i]?.description || '',
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);
  const [fiches, setFiches] = useState(initial);
  const [savingIdx, setSavingIdx] = useState(null);

  const update = (idx, field, value) => {
    setFiches((prev) => prev.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };

  const save = async (idx) => {
    setSavingIdx(idx);
    try {
      const f = fiches[idx];
      await api.patch(`/projects/${project.id}/fiches`, {
        video_idx: idx,
        cover_url: f.cover_url || '',
        title: f.title || '',
        description: f.description || '',
      });
      toast.success(`Vidéo ${idx + 1} enregistrée`);
      onChanged && onChanged();
    } catch { toast.error('Erreur'); }
    finally { setSavingIdx(null); }
  };

  if (fiches.length === 0) {
    return (
      <div style={{ padding: 12, color: 'var(--t3)', fontFamily: 'Lexend', fontSize: '0.82rem' }}>
        Aucune vidéo. La fiche de poste est désactivée pour ce projet.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontFamily: 'Lexend', fontSize: '0.72rem', color: 'var(--t3)', marginBottom: 4 }}>
        Une fiche par vidéo. Tout est optionnel. Pratique pour la programmation des publications.
      </div>
      {fiches.map((f, idx) => (
        <div key={idx} className="asa-card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 24, height: 24, borderRadius: 6,
              background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.35)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <FileText size={12} color="#A78BFA" />
            </div>
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: '0.78rem', color: 'var(--t1)' }}>
              Vidéo {idx + 1}
            </span>
            <button
              onClick={() => save(idx)}
              disabled={savingIdx === idx}
              data-testid={`fiche-save-${idx}`}
              style={{
                marginLeft: 'auto', padding: '4px 10px', borderRadius: 999,
                background: 'rgba(46,163,242,0.15)', color: 'var(--pl)',
                border: '1px solid rgba(46,163,242,0.3)',
                fontFamily: 'Sora', fontWeight: 600, fontSize: '0.66rem',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              {savingIdx === idx ? <Loader2 size={10} className="spin" /> : <Check size={10} />}
              Enregistrer
            </button>
          </div>
          <input
            data-testid={`fiche-cover-${idx}`}
            type="url"
            placeholder="Lien de la couverture (image, drive…)"
            value={f.cover_url}
            onChange={(e) => update(idx, 'cover_url', e.target.value)}
            style={ficheInputStyle}
          />
          <input
            data-testid={`fiche-title-${idx}`}
            placeholder="Titre de la vidéo"
            value={f.title}
            onChange={(e) => update(idx, 'title', e.target.value)}
            style={ficheInputStyle}
          />
          <textarea
            data-testid={`fiche-desc-${idx}`}
            placeholder="Description (caption, CTA…)"
            value={f.description}
            onChange={(e) => update(idx, 'description', e.target.value)}
            rows={2}
            style={{ ...ficheInputStyle, resize: 'vertical', minHeight: 50 }}
          />
        </div>
      ))}
    </div>
  );
}

const projInputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 10,
  fontFamily: 'Lexend', fontSize: '0.86rem', color: 'var(--t1)',
  marginBottom: 12,
};

const ficheInputStyle = {
  width: '100%', padding: '8px 11px',
  background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 8,
  fontFamily: 'Lexend', fontSize: '0.8rem', color: 'var(--t1)',
};

// ===========================================================================
// ClientPicker — autocomplete existing clients OR create new one with color
// ===========================================================================
const CLIENT_COLORS = [
  '#A78BFA', // violet
  '#5BBEFC', // cyan
  '#34D399', // green
  '#F5A623', // yellow
  '#FF6B6B', // red
  '#EC4899', // pink
  '#8B5CF6', // indigo
  '#FCD34D', // gold (Nash?)
  '#FB923C', // orange
];

function ClientPicker({ clients, value, color, onPickExisting, onChangeName, onChangeColor, onCreate }) {
  const [open, setOpen] = useState(false);
  const v = (value || '').toLowerCase();
  const matches = clients.filter((c) => c.name.toLowerCase().includes(v));
  const exactMatch = clients.find((c) => c.name.toLowerCase() === v);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Color preview */}
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: color, flexShrink: 0,
          border: '1.5px solid var(--bd)',
          boxShadow: `0 0 0 3px ${color}22`,
        }} />
        <input
          data-testid="fw-client"
          placeholder="Tape ou choisis un client…"
          value={value}
          onChange={(e) => onChangeName(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          style={{ flex: 1, fontFamily: 'Lexend', fontSize: '0.92rem' }}
        />
      </div>

      {/* Color swatches */}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {CLIENT_COLORS.map((c) => {
          const active = c === color;
          return (
            <button
              key={c}
              type="button"
              data-testid={`client-color-${c}`}
              onClick={() => onChangeColor(c)}
              title={c}
              style={{
                width: 24, height: 24, borderRadius: 6,
                background: c, cursor: 'pointer',
                border: active ? '2px solid #fff' : '1.5px solid var(--bd)',
                boxShadow: active ? `0 0 0 2px ${c}66` : 'none',
                padding: 0,
              }}
            />
          );
        })}
      </div>

      {open && (matches.length > 0 || (value && !exactMatch)) && (
        <div style={{
          position: 'absolute', left: 46, right: 0, top: 44, zIndex: 20,
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 11,
          boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
          padding: 4, maxHeight: 240, overflowY: 'auto',
        }}>
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`client-opt-${c.id}`}
              onMouseDown={(e) => { e.preventDefault(); onPickExisting(c); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '7px 10px', borderRadius: 7,
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: 4, background: c.color, flexShrink: 0 }} />
              <span style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: '0.82rem', color: 'var(--t1)' }}>{c.name}</span>
            </button>
          ))}
          {value && !exactMatch && (
            <button
              type="button"
              data-testid="client-create"
              onMouseDown={(e) => { e.preventDefault(); onCreate(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '8px 10px', borderRadius: 7, marginTop: 4,
                background: 'rgba(46,163,242,0.10)', border: '1px dashed rgba(46,163,242,0.4)',
                cursor: 'pointer', textAlign: 'left',
                color: 'var(--pl)', fontFamily: 'Sora', fontWeight: 600, fontSize: '0.78rem',
              }}
            >
              <Plus size={12} /> Créer le client « {value} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// ProjectRequestDialog — contributors submit a project idea to founders
// ---------------------------------------------------------------------------
function ProjectRequestDialog({ onClose, onSaved }) {
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Donne un nom au projet'); return; }
    setBusy(true);
    try {
      await api.post('/project-requests', { name: name.trim(), client: client.trim(), note: note.trim() });
      onSaved();
    } catch { toast.error('Erreur lors de la demande'); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(6,11,24,0.62)',
        backdropFilter: 'blur(8px)', zIndex: 110,
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-up"
        data-testid="project-request-dialog"
        style={{
          width: '100%', maxWidth: 460,
          background: 'var(--sf)',
          border: '1.5px solid var(--bd)', borderRadius: 18, padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <h3 className="t-display-sm" style={{ margin: 0, flex: 1 }}>Demander un projet</h3>
          <button onClick={onClose}><X size={16} color="var(--t3)" /></button>
        </div>
        <p style={{
          fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)',
          margin: '0 0 18px',
        }}>Ta demande sera envoyée à Rayan et Hugo pour validation.</p>

        <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Nom du projet *</div>
        <div className="asa-field" style={{ marginBottom: 14 }}>
          <input
            data-testid="req-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex. Podcast Hakim — Lot Juin"
            style={{ flex: 1, fontSize: 14 }}
          />
        </div>

        <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Client</div>
        <div className="asa-field" style={{ marginBottom: 14 }}>
          <input
            data-testid="req-client"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            placeholder="Nom du client (optionnel)"
            style={{ flex: 1, fontSize: 14 }}
          />
        </div>

        <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Note pour les fondateurs</div>
        <textarea
          data-testid="req-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Pourquoi ce projet, échéance, format, ressources…"
          rows={4}
          style={{
            width: '100%', padding: '10px 12px',
            background: 'var(--sf2)', border: '1.5px solid var(--bd)',
            borderRadius: 10, color: 'var(--t1)',
            fontFamily: 'Lexend', fontSize: 13, resize: 'vertical',
            marginBottom: 18,
          }}
        />

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 10,
              background: 'transparent', border: '1.5px solid var(--bd)',
              color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
            }}
          >Annuler</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="req-submit"
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 10,
              background: 'var(--p)', border: 'none',
              color: '#fff', fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
              opacity: busy ? 0.6 : 1,
            }}
          >{busy ? 'Envoi…' : 'Envoyer la demande'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectRequestsInbox — founders review pending requests
// ---------------------------------------------------------------------------
function ProjectRequestsInbox({ requests, users, onClose, onChanged }) {
  const pending = requests.filter((r) => r.status === 'pending');
  const decide = async (rid, action) => {
    try {
      await api.post(`/project-requests/${rid}/${action}`);
      toast.success(action === 'approve' ? 'Demande approuvée' : 'Demande refusée');
      onChanged();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(6,11,24,0.62)',
        backdropFilter: 'blur(8px)', zIndex: 110,
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-up"
        data-testid="project-requests-inbox-dialog"
        style={{
          width: '100%', maxWidth: 540,
          maxHeight: '80vh', overflowY: 'auto',
          background: 'var(--sf)',
          border: '1.5px solid var(--bd)', borderRadius: 18, padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h3 className="t-display-sm" style={{ margin: 0, flex: 1 }}>Demandes de projets</h3>
          <button onClick={onClose}><X size={16} color="var(--t3)" /></button>
        </div>

        {pending.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '32px 0',
            fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)',
          }}>
            Aucune demande en attente
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pending.map((r) => {
              const requester = users.find((u) => u.id === r.requested_by);
              return (
                <div
                  key={r.id}
                  data-testid={`request-${r.id}`}
                  style={{
                    padding: 14, borderRadius: 12,
                    background: 'var(--sf2)',
                    border: '1.5px solid var(--bd)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    {requester && <Avatar user={requester} size={26} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>
                        {r.name}
                      </div>
                      <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)' }}>
                        Demandé par {requester?.name || r.requested_by}
                        {r.client && ` · Client : ${r.client}`}
                      </div>
                    </div>
                  </div>
                  {r.note && (
                    <div style={{
                      fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t2)',
                      padding: '8px 10px', borderRadius: 8,
                      background: 'var(--sf)', marginBottom: 10,
                      whiteSpace: 'pre-wrap',
                    }}>{r.note}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      data-testid={`request-${r.id}-reject`}
                      onClick={() => decide(r.id, 'reject')}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 9,
                        background: 'transparent',
                        border: '1.5px solid rgba(255,107,107,0.4)',
                        color: '#FF6B6B',
                        fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
                      }}
                    >Refuser</button>
                    <button
                      data-testid={`request-${r.id}-approve`}
                      onClick={() => decide(r.id, 'approve')}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 9,
                        background: '#34D399',
                        border: 'none',
                        color: '#0A1A33',
                        fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                      }}
                    >Approuver</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// FromDocDialog — auto-generate a project + tasks from a pasted document (LLM)
// After AI extraction, the SAME unified ProjectDialog is shown pre-filled so
// the user can edit every field (name, dates, assignees, links, etc.).
// ---------------------------------------------------------------------------
function FromDocDialog({ folderId, users = [], onClose, onSaved }) {
  const [text, setText] = useState('');
  const [driveLink, setDriveLink] = useState('');
  const [swiftLink, setSwiftLink] = useState('');
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (text.trim().length < 20) { toast.error('Texte trop court (au moins 20 caractères)'); return; }
    setGenerating(true);
    try {
      const r = await api.post('/projects/from-doc', {
        text: text.trim(),
        folder: folderId || 'internal',
        drive_link: driveLink.trim() || null,
        swift_link: swiftLink.trim() || null,
      });
      setDraft(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur IA'); }
    finally { setGenerating(false); }
  };

  // After AI generation → show the SAME unified ProjectDialog pre-filled
  if (draft) {
    const initialValues = {
      name: draft.name || '',
      client: draft.client || '',
      description: draft.description || '',
      icon: 'video',
      color: '#A78BFA',
      folder: draft.folder || folderId || 'internal',
      drive_links: draft.drive_link ? [{ name: 'Drive', url: draft.drive_link }] : [],
      swift_links: draft.swift_link ? [{ name: 'SwissTransfer', url: draft.swift_link }] : [],
      assignees: [],
      start_date: '',
      end_date: '',
      tags: '',
      video_count: String(draft.tasks?.length || ''),
    };
    return (
      <ProjectDialog
        users={users}
        folderId={folderId}
        initialValues={initialValues}
        pendingTasks={draft.tasks || []}
        modalTitle="Vérifier le projet IA"
        submitLabel={`Créer (${draft.tasks?.length || 0} tâches)`}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  }

  return (
    <Modal title="Générer depuis un document" onClose={onClose}>
      <p style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', margin: '0 0 12px' }}>
        Colle ici un brief client, un mail, un debrief de réunion ou un texte libre.
        Claude Sonnet 4.6 va en extraire un projet et ses tâches. Tu pourras ensuite éditer chaque champ.
      </p>

      <div className="asa-eyebrow" style={{ marginBottom: 6 }}>
        <Folder size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        Lien Drive (rushes) — optionnel
      </div>
      <div className="asa-field" style={{ marginBottom: 10 }}>
        <input
          data-testid="from-doc-drive"
          value={driveLink}
          onChange={(e) => setDriveLink(e.target.value)}
          placeholder="https://drive.google.com/..."
          style={{ flex: 1, fontSize: 12.5 }}
        />
      </div>

      <div className="asa-eyebrow" style={{ marginBottom: 6 }}>
        <LinkIcon size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        Lien SwissTransfer / WeTransfer — optionnel
      </div>
      <div className="asa-field" style={{ marginBottom: 14 }}>
        <input
          data-testid="from-doc-swift"
          value={swiftLink}
          onChange={(e) => setSwiftLink(e.target.value)}
          placeholder="https://www.swisstransfer.com/..."
          style={{ flex: 1, fontSize: 12.5 }}
        />
      </div>

      <div className="asa-eyebrow" style={{ marginBottom: 6 }}>Document à analyser *</div>
      <textarea
        data-testid="from-doc-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Colle ton document ici…"
        rows={10}
        style={{
          width: '100%', padding: '11px 13px',
          background: 'var(--sf2)', border: '1.5px solid var(--bd)',
          borderRadius: 11, color: 'var(--t1)',
          fontFamily: 'Lexend', fontSize: 13, lineHeight: 1.55,
          resize: 'vertical', marginBottom: 14,
          minHeight: 180,
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose} style={{
          flex: 1, padding: '10px 14px', borderRadius: 10,
          background: 'transparent', border: '1.5px solid var(--bd)',
          color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
        }}>Annuler</button>
        <button
          onClick={generate}
          disabled={generating || text.trim().length < 20}
          data-testid="from-doc-generate"
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 10,
            background: '#A78BFA', border: 'none',
            color: '#fff', fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
            opacity: (generating || text.trim().length < 20) ? 0.5 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {generating
            ? <><Loader2 size={14} className="spin" /> Génération…</>
            : <><Sparkles size={14} /> Générer</>}
        </button>
      </div>
    </Modal>
  );
}

// Helper: short format label for task title prefix
function formatLabelLight(f) {
  return ({ podcast: 'Podcast', facecam: 'Facecam', technique: 'Technique', special: 'Spécial' }[f] || 'Vidéo');
}


// Iter47l — Client folder picker shown BEFORE the ProjectWizardV3.
// Lists all existing project client names + option "+ Nouveau client".
function ClientFolderPicker({ projects, onClose, onPick }) {
  const [newClient, setNewClient] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const clientsMap = new Map(); // name -> count
  (projects || []).forEach((p) => {
    const c = (p.client || '').trim();
    if (!c) return;
    clientsMap.set(c, (clientsMap.get(c) || 0) + 1);
  });
  const clients = Array.from(clientsMap.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

  return (
    <div
      onClick={onClose}
      data-testid="client-picker-backdrop"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(10,26,51,0.70)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 18px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        data-testid="client-picker-modal"
        style={{
          width: '100%', maxWidth: 520,
          maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 22,
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-xl)', flex: 1 }}>
            Nouveau projet — quel client ?
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        <p style={{ margin: '0 0 20px', fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)' }}>
          Choisis un dossier client existant ou crée-en un nouveau.
        </p>

        {clients.length > 0 && (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Dossiers existants</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {clients.map(([name, count]) => (
                <button
                  key={name}
                  type="button"
                  data-testid={`client-pick-${name}`}
                  onClick={() => onPick(name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '11px 14px', borderRadius: 11,
                    background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                    cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5BBEFC'; e.currentTarget.style.background = 'rgba(91,190,252,0.06)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = 'var(--sf2)'; }}
                >
                  <Folder size={16} color="var(--pl)" strokeWidth={1.6} />
                  <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>{name}</span>
                  <span style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)',
                    padding: '2px 8px', borderRadius: 999, background: 'var(--sf)',
                  }}>{count} projet{count > 1 ? 's' : ''}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="asa-eyebrow" style={{ marginBottom: 8 }}>Ou nouveau client</div>
        {creatingNew ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              autoFocus
              data-testid="client-new-input"
              placeholder="Nom du client (ex : Nash, Hakim, Benjamin)"
              value={newClient}
              onChange={(e) => setNewClient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newClient.trim()) onPick(newClient.trim());
                if (e.key === 'Escape') { setCreatingNew(false); setNewClient(''); }
              }}
              style={{
                flex: 1, padding: '11px 14px',
                background: 'var(--sf2)', border: '1.5px solid var(--bd)', borderRadius: 11,
                fontFamily: 'Lexend', fontSize: 14, color: 'var(--t1)',
              }}
            />
            <button
              type="button"
              data-testid="client-new-confirm"
              onClick={() => newClient.trim() && onPick(newClient.trim())}
              disabled={!newClient.trim()}
              className="asa-btn"
              style={{ opacity: newClient.trim() ? 1 : 0.5 }}
            >Continuer</button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="client-new-btn"
            onClick={() => setCreatingNew(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%',
              padding: '11px 14px', borderRadius: 11,
              background: 'var(--sf2)', border: '1.5px dashed var(--bd)',
              cursor: 'pointer', textAlign: 'left',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5BBEFC'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; }}
          >
            <Plus size={16} color="var(--pl)" strokeWidth={2} />
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t2)' }}>
              Créer un nouveau dossier client
            </span>
          </button>
        )}
      </div>
    </div>
  );
}


// ─── Iter 62fk — StageDropdown : bouton de statut avec dropdown pour vidéos ───
// Clic sur le pill → menu avec les 5 statuts (À faire / En cours / À valider / À poster / Terminé).
// Si "À poster" sélectionné → ouvre PosterPickerModal pour choisir qui va poster.
function StageDropdown({ task, onPickStage }) {
  const [open, setOpen] = useState(false);
  const [showPoster, setShowPoster] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const pillRef = useRef(null);
  const s = STAGES[task.stage] || STAGES.todo;
  const options = STAGE_ORDER.map((id) => STAGES[id]);
  const openMenu = (e) => {
    e.stopPropagation();
    const rect = pillRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 4, left: rect.left });
    setOpen(true);
  };
  const handlePick = (stageId) => {
    setOpen(false);
    if (stageId === 'to_post') { setShowPoster(true); return; }
    onPickStage(stageId);
  };
  return (
    <>
      <button
        ref={pillRef}
        data-testid={`stage-pill-${task.id}`}
        onClick={openMenu}
        style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: '0.7rem',
          color: s.color, display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 8px', borderRadius: 999,
          background: `${s.color}1A`, border: `1px solid ${s.color}44`,
          cursor: 'pointer',
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color }} />
        {s.label}
        <span style={{ fontSize: '0.6rem', opacity: 0.6, marginLeft: 2 }}>▾</span>
      </button>
      {open && menuPos && createPortal(
        <>
          <div onClick={(e) => { e.stopPropagation(); setOpen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 160 }} />
          <div
            data-testid={`stage-menu-${task.id}`}
            style={{
              position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 170,
              background: 'linear-gradient(180deg, rgba(20,29,66,0.98) 0%, rgba(14,22,44,0.99) 100%)',
              border: '1px solid var(--bd)', borderRadius: 10, padding: 4,
              boxShadow: '0 24px 50px -14px rgba(0,0,0,0.6)',
              minWidth: 160, display: 'flex', flexDirection: 'column', gap: 1,
            }}
          >
            {options.map((o) => (
              <button
                key={o.id}
                data-testid={`stage-opt-${task.id}-${o.id}`}
                onClick={(e) => { e.stopPropagation(); handlePick(o.id); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 10px', borderRadius: 7,
                  background: task.stage === o.id ? `${o.color}22` : 'transparent',
                  border: 'none', color: 'var(--t1)', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                }}
                onMouseEnter={(e) => { if (task.stage !== o.id) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={(e) => { if (task.stage !== o.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: o.color }} />
                <span style={{ color: o.color, flex: 1 }}>{o.label}</span>
                {task.stage === o.id && <span style={{ color: o.color }}>✓</span>}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
      {showPoster && createPortal(
        <PosterPickerModal
          task={task}
          onClose={() => setShowPoster(false)}
          onConfirm={(posterId, postDate) => {
            setShowPoster(false);
            onPickStage('to_post', { assigned_to_poster: posterId, scheduled_post_date: postDate });
          }}
        />,
        document.body
      )}
    </>
  );
}

const badPub = (t) => ['error', 'unsure', 'copied'].includes(t.pub_check?.status);

function PosterPickerModal({ task, onClose, onConfirm }) {
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState(task.assigned_to_poster || task.assigned_to_montage || '');
  // Iter 62fr — Si pas encore de poster sélectionné, on fetch le projet pour
  // utiliser son `poster_id` par défaut. Ça évite au founder de re-sélectionner
  // le poster à chaque passage "À poster" quand le projet a un posteur attitré.
  useEffect(() => {
    if (picked) return; // déjà un choix explicite
    const pid = task.project_id;
    if (!pid) return;
    let cancelled = false;
    api.get(`/projects/${pid}`).then((r) => {
      if (cancelled) return;
      const def = r?.data?.poster_id;
      if (def) setPicked(def);
    }).catch(() => { /* noop */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Iter 62fq — Date de post intelligente avec rattrapage du retard de montage.
  // Formule : post_date = max(today + 1, due_date_initiale + 7)
  //  → Si livré à temps : post à J+7 (règle "7 jours après fin montage").
  //  → Si 1 jour de retard : post à J+6 (rattrape).
  //  → Si 2 jours : J+5. 4 jours : J+3. 7 jours ou plus : J+1 (demain).
  // Ça garantit que le planning initial est respecté quand c'est possible,
  // et sinon qu'on poste dès que possible pour minimiser l'impact.
  // Jour de post = jour prévu de la vidéo (deadline), ou aujourd'hui si déjà passé.
  const [postDate, setPostDate] = useState(() => {
    const n = new Date(); const today = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
    const dueIso = (task.due_date || '').slice(0, 10);
    return dueIso && dueIso > today ? dueIso : today;
  });
  const [rhythmSlot, setRhythmSlot] = useState(null);
  useEffect(() => {
    if (!task.project_id) return;
    api.get(`/projects/${task.project_id}/next-post-slot`, { params: { exclude: task.id, due: (task.due_date || '').slice(0, 10) || undefined } })
      .then((r) => { if (r.data?.date) { setRhythmSlot(r.data.date !== postDate ? r.data.date : null); setPostDate(r.data.date); } }).catch(() => {});
  }, [task.id, task.project_id]);
  // Calcule le retard & le label dynamique pour le hint
  const retardInfo = useMemo(() => {
    const dueIso = (task.due_date || '').slice(0, 10);
    if (!dueIso) return { label: "aujourd'hui", hasRetard: false };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(`${dueIso}T00:00:00`);
    const retard = Math.floor((today - due) / 86400000);
    if (retard <= 0) return { label: 'le jour prévu', hasRetard: false };
    return { label: `aujourd'hui (${retard}j de retard)`, hasRetard: true };
  }, [task.due_date]);
  useEffect(() => {
    api.get('/users').then((r) => setUsers((r.data || []).filter((u) => !u.is_archived && u.role !== 'client'))).catch(() => setUsers([]));
  }, []);
  return (
    <div
      data-testid="poster-picker-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 150,
        background: 'rgba(6,12,26,0.72)', backdropFilter: 'blur(10px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 440,
          background: 'linear-gradient(180deg, rgba(20,29,66,0.72) 0%, rgba(14,22,44,0.98) 100%)',
          borderRadius: 22, border: '1px solid var(--bd)',
          padding: 24, boxShadow: '0 40px 80px -20px rgba(0,0,0,0.65)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 32, marginBottom: 6 }}>📱</div>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: 'var(--t1)', margin: 0 }}>À poster</h2>
          <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 11.5, color: 'var(--t3)', marginTop: 6, lineHeight: 1.4 }}>
            La vidéo sort du projet et devient une tâche "📱 Poster {task.title}" dans les tâches du poster.
          </div>
        </div>
        {/* Date de post */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Date de post</div>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: retardInfo.hasRetard ? '#FF8A5B' : '#F5C56A', letterSpacing: '0.02em', textAlign: 'right' }}>
            {rhythmSlot ? '🔁 Prochain jour libre selon le rythme du projet (pas d’empilement)' : <>{retardInfo.hasRetard ? '⚠️ ' : ''}Pré-rempli à {retardInfo.label}</>}
          </div>
        </div>
        <input
          type="date"
          data-testid="post-date-input"
          value={postDate}
          onChange={(e) => setPostDate(e.target.value)}
          style={{
            width: '100%', padding: '11px 13px', borderRadius: 10,
            background: 'var(--sf)', border: '1px solid var(--bd)',
            color: 'var(--t1)', outline: 'none',
            fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
            marginBottom: 16,
          }}
        />
        {/* Qui va poster */}
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Qui va poster</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
          {users.map((u) => {
            const active = picked === u.id;
            return (
              <button
                key={u.id}
                data-testid={`poster-pick-${u.id}`}
                onClick={() => setPicked(u.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 11,
                  background: active ? 'rgba(245,197,106,0.14)' : 'var(--sf)',
                  border: `1px solid ${active ? '#F5C56A' : 'var(--bd)'}`,
                  color: 'var(--t1)', cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.12s var(--ease-out)',
                }}
              >
                <span style={{
                  width: 30, height: 30, borderRadius: 9,
                  background: active ? 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)' : 'linear-gradient(180deg, #5BBEFC 0%, #3E7BFA 100%)',
                  color: '#0B1330', display: 'grid', placeItems: 'center',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 12, flexShrink: 0,
                }}>{(u.short || u.name || '?')[0].toUpperCase()}</span>
                <span style={{ flex: 1, fontFamily: 'Sora', fontWeight: 700, fontSize: 13 }}>{u.name}</span>
                {active && <span style={{ color: '#F5C56A', fontWeight: 800 }}>✓</span>}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '12px 16px', borderRadius: 12, background: 'transparent', border: '1px solid var(--bd)', color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >Annuler</button>
          <button
            type="button"
            data-testid="poster-confirm"
            disabled={!picked || !postDate}
            onClick={() => onConfirm(picked, postDate)}
            style={{
              padding: '12px 16px', borderRadius: 12,
              background: (picked && postDate) ? 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)' : 'var(--sf)',
              border: 'none', color: (picked && postDate) ? '#0B1330' : 'var(--t3)',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
              cursor: (picked && postDate) ? 'pointer' : 'not-allowed',
              boxShadow: (picked && postDate) ? '0 8px 20px -6px rgba(245,197,106,0.55), inset 0 1px 0 rgba(255,255,255,0.28)' : 'none',
            }}
          >Confirmer</button>
        </div>
      </div>
    </div>
  );
}


// ─── Iter 62fl — ProjectKanban : 5 colonnes drag & drop (todo / in_progress / review / to_post / done)
// Bouton qui ouvre une petite liste d'actions (regroupe Rangement / Post).
function ActionMenu({ label, icon, rgb, items, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const shown = items.filter((i) => !i.hidden);
  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 180 }}>
      <button data-testid={testid} onClick={() => setOpen((x) => !x)} style={{
        width: '100%', padding: '12px 16px', background: `rgba(${rgb},0.10)`, border: `1.5px solid rgba(${rgb},0.45)`,
        borderRadius: 12, color: `rgb(${rgb})`, fontFamily: 'Sora', fontWeight: 700, fontSize: '0.84rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
      }}>{icon} {label} <span style={{ opacity: 0.7 }}>· {shown.length}</span> <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} /></button>
      {open && (
        <div data-testid={`${testid}-list`} className="scale-in" style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: 0, right: 0, minWidth: 260, zIndex: 60, padding: 6, borderRadius: 14, background: 'rgba(14,22,44,0.98)', border: `1px solid rgba(${rgb},0.35)`, boxShadow: '0 20px 40px -10px rgba(0,0,0,0.6)', backdropFilter: 'blur(16px)' }}>
          {shown.map((i) => (
            <button key={i.testid} data-testid={i.testid} disabled={i.disabled} onClick={() => { setOpen(false); i.onClick(); }} style={{
              width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10, border: 'none', background: 'transparent',
              color: i.danger ? '#FF8A8A' : 'var(--t1)', fontFamily: 'Sora', fontWeight: 600, fontSize: 12.5,
              cursor: i.disabled ? 'not-allowed' : 'pointer', opacity: i.disabled ? 0.45 : 1,
            }} onMouseEnter={(e) => { e.currentTarget.style.background = `rgba(${rgb},0.12)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>{i.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const REDO_META = { id: 'redo', label: 'En correction', color: '#F87171' };
const KANBAN_COLS = ['todo', 'redo', 'in_progress', 'review', 'to_post', 'done'];
const kanbanCol = (t) => ((t.stage || 'todo') === 'in_progress' && t.review_status === 'redo' ? 'redo' : (t.stage || 'todo'));
const dateField = (t) => (t.stage === 'to_post' ? 'scheduled_post_date' : 'due_date');
const cardDate = (t) => (t[dateField(t)] || '').slice(0, 10);
// Échange strict des dates de 2 vidéos.
async function swapVideoDates(a, b, onChanged) {
  const fa = dateField(a), fb = dateField(b);
  if (!a[fa] && !b[fb]) return;
  try {
    await api.patch(`/tasks/${a.id}`, b[fb] ? { [fa]: b[fb].slice(0, 10) } : { clear_due_date: true });
    await api.patch(`/tasks/${b.id}`, a[fa] ? { [fb]: a[fa].slice(0, 10) } : { clear_due_date: true });
    toast.success('Dates échangées ✓');
    onChanged();
  } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
}
// Dépôt rapide : la vidéo prend la place de la carte visée, les dates de la file restent et suivent le nouvel ordre.
async function insertVideoAt(list, task, overTask, onChanged) {
  const dates = list.map((t) => t[dateField(t)] || null);
  const from = list.findIndex((t) => t.id === task.id);
  const to = list.findIndex((t) => t.id === overTask.id);
  const order = list.filter((t) => t.id !== task.id);
  order.splice(to, 0, task);
  const changes = order.map((t, i) => [t, dates[i]]).filter(([t, d]) => d && (t[dateField(t)] || '').slice(0, 10) !== d.slice(0, 10));
  if (from < 0 || to < 0 || from === to || !changes.length) return false;
  try {
    await Promise.all(changes.map(([t, d]) => api.patch(`/tasks/${t.id}`, { [dateField(t)]: d.slice(0, 10) })));
    toast.success(`Ordre mis à jour · ${changes.length} date(s) décalée(s) ✓`);
    onChanged();
  } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  return true;
}

function ProjectKanban({ tasks, onChanged, onEdit }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [dragged, setDragged] = useState(null);
  const [showPosterFor, setShowPosterFor] = useState(null);
  const columns = useMemo(() => KANBAN_COLS.map((id) => ({
    id, meta: id === 'redo' ? REDO_META : STAGES[id],
    items: tasks.filter((t) => kanbanCol(t) === id).sort((a, b) => (cardDate(a) || '9999').localeCompare(cardDate(b) || '9999')),
  })), [tasks]);
  const swapDates = (a, b) => swapVideoDates(a, b, onChanged);
  const insertAt = (task, overTask, col) => insertVideoAt(columns.find((c) => c.id === col).items, task, overTask, onChanged);
  // Rester ~2 s sur une carte → proposition d'échange de dates.
  const [swapReady, setSwapReady] = useState(null);
  const hoverRef = useRef({ id: null, timer: null });
  const clearHover = () => { clearTimeout(hoverRef.current.timer); hoverRef.current = { id: null, timer: null }; setSwapReady(null); };
  const handleDragOver = (e) => {
    const overId = e.over ? String(e.over.id) : '';
    const id = overId.startsWith('card-') ? overId.slice(5) : null;
    if (id === hoverRef.current.id) return;
    clearHover();
    if (!id || id === e.active.id) return;
    hoverRef.current = { id, timer: setTimeout(() => setSwapReady(id), 2000) };
  };
  const handleDragEnd = async (e) => {
    const { active, over } = e;
    setDragged(null);
    if (!active || !over) return;
    const task = tasks.find((t) => t.id === active.id);
    if (!task) return;
    const overId = String(over.id);
    const overTask = overId.startsWith('card-') ? tasks.find((t) => `card-${t.id}` === overId) : null;
    if (overTask && overTask.id === task.id) return;
    const target = overTask ? kanbanCol(overTask) : overId.replace(/^col-/, '');
    const from = kanbanCol(task);
    if (overTask && target === from) {
      const ready = swapReady === overTask.id;
      clearHover();
      if (ready) { if (window.confirm('Est-ce que vous voulez faire un switch de date entre ces 2 vidéos ?')) swapDates(task, overTask); return; }
      insertAt(task, overTask, from);
      return;
    }
    clearHover();
    if (target === from || !(STAGES[target] || target === 'redo')) return;
    if (target === 'to_post') { setShowPosterFor(task); return; }
    const dx = target === 'redo' ? {} : await stageDeliveryExtra(task, target, CAN_MANAGE.current);
    if (!dx) return;
    const body = target === 'redo' ? { stage: 'in_progress', review_status: 'redo' } : { stage: target, ...(from === 'redo' ? { clear_review: true } : {}), ...dx };
    try {
      await api.patch(`/tasks/${task.id}`, body);
      toast.success(target === 'redo' ? 'Vidéo en correction' : 'Statut mis à jour');
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <>
      <DndContext sensors={sensors} onDragStart={(e) => setDragged(tasks.find((t) => t.id === e.active.id))} onDragOver={handleDragOver} onDragCancel={() => { clearHover(); setDragged(null); }} onDragEnd={handleDragEnd}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns.length}, minmax(220px, 1fr))`, gap: 12, overflowX: 'auto', paddingBottom: 10 }}>
          {columns.map((col) => (
            <KanbanColumn key={col.id} column={col} onEdit={onEdit} onChanged={onChanged} swapReady={swapReady} />
          ))}
        </div>
        <DragOverlay>
          {dragged && <KanbanCardPreview task={dragged} />}
        </DragOverlay>
      </DndContext>
      {showPosterFor && createPortal(
        <PosterPickerModal
          task={showPosterFor}
          onClose={() => setShowPosterFor(null)}
          onConfirm={async (posterId, postDate) => {
            const t = showPosterFor;
            setShowPosterFor(null);
            try {
              await api.patch(`/tasks/${t.id}`, { stage: 'to_post', assigned_to_poster: posterId, scheduled_post_date: postDate });
              toast.success('Vidéo prête à poster');
              onChanged();
            } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
          }}
        />,
        document.body
      )}
    </>
  );
}

function KanbanColumn({ column, onEdit, onChanged, swapReady }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${column.id}` });
  const { meta, items } = column;
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${column.id}`}
      style={{
        background: isOver ? `${meta.color}14` : 'var(--sf)',
        border: `1px solid ${isOver ? meta.color : 'var(--bd)'}`,
        borderRadius: 14, padding: 10,
        minHeight: 320,
        transition: 'all 0.15s var(--ease-out)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${meta.color}33` }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, boxShadow: `0 0 8px ${meta.color}aa` }} />
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, color: meta.color, flex: 1, letterSpacing: '-0.005em' }}>{meta.label}</div>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--t3)', fontWeight: 700 }}>{items.length}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.length === 0 ? (
          <div style={{ padding: '18px 10px', textAlign: 'center', fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: 'var(--t4)', fontStyle: 'italic' }}>
            Drag une vidéo ici
          </div>
        ) : (
          items.map((t) => <KanbanCard key={t.id} task={t} onEdit={onEdit} onChanged={onChanged} color={meta.color} swapReady={swapReady === t.id} />)
        )}
      </div>
    </div>
  );
}

// « À valider » : on choisit une date… ou pas de date du tout.
function ReviewDateControl({ task, onChanged }) {
  const [open, setOpen] = useState(false);
  const save = async (payload, msg) => {
    setOpen(false);
    try {
      await api.patch(`/tasks/${task.id}`, payload);
      toast.success(msg);
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };
  const stop = (e) => e.stopPropagation();
  const label = task.due_date ? new Date(task.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : 'Sans date';
  return (
    <div onPointerDown={stop} onMouseDown={stop} onTouchStart={stop} onClick={stop} style={{ position: 'relative' }}>
      <span
        role="button"
        tabIndex={0}
        data-testid={`review-date-chip-${task.id}`}
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, cursor: 'pointer', background: task.due_date ? 'rgba(167,139,250,0.14)' : 'rgba(255,255,255,0.05)', border: `1px dashed ${task.due_date ? 'rgba(167,139,250,0.45)' : 'var(--bd)'}`, color: task.due_date ? '#C4B5FD' : 'var(--t3)', fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5 }}
      ><Calendar size={11} /> {label}</span>
      {open && (
        <div data-testid={`review-date-popover-${task.id}`} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 30, padding: 10, borderRadius: 12, background: 'rgba(14,22,44,0.98)', border: '1px solid var(--bd)', boxShadow: '0 18px 40px -10px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 190 }}>
          <input
            type="date"
            data-testid={`review-date-input-${task.id}`}
            defaultValue={(task.due_date || '').slice(0, 10)}
            onChange={(e) => e.target.value && save({ due_date: e.target.value }, 'Date mise à jour')}
            style={{ fontFamily: 'Lexend', fontSize: 12.5, colorScheme: 'dark' }}
          />
          <button
            type="button"
            data-testid={`review-date-clear-${task.id}`}
            onClick={() => save({ clear_due_date: true }, 'Vidéo sans date — retirée du planning')}
            style={{ padding: '7px 10px', borderRadius: 9, border: '1px solid rgba(255,107,107,0.35)', background: 'transparent', color: '#FF8A8A', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5 }}
          >Pas de date pour l&apos;instant</button>
        </div>
      )}
    </div>
  );
}

function KanbanCard({ task, onEdit, onChanged, color, swapReady }) {
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({ id: task.id });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `card-${task.id}` });
  const setNodeRef = (n) => { setDragRef(n); setDropRef(n); };
  const prio = task.priority === 'high' ? '#FF6B6B' : task.priority === 'medium' ? '#F5A623' : null;
  return (
    <div
      role="button"
      tabIndex={0}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`kanban-card-${task.id}`}
      onClick={(e) => { e.stopPropagation(); if (!isDragging) onEdit?.(task); }}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left',
        padding: '10px 12px', borderRadius: 10,
        background: 'linear-gradient(180deg, rgba(20,29,66,0.78) 0%, rgba(14,22,44,0.95) 100%)',
        border: `1px solid ${color}30`,
        borderLeft: `3px solid ${(task.stage === 'review' && DELIVERY_STYLE[task.delivery]?.color) || color}`,
        color: 'var(--t1)', cursor: isDragging ? 'grabbing' : 'grab',
        boxShadow: isDragging ? `0 20px 40px -8px rgba(0,0,0,0.6)` : swapReady ? '0 0 0 3px #34D399' : isOver ? `0 0 0 2px #F5C56A` : `0 4px 10px -3px ${color}22`,
        opacity: isDragging ? 0.3 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: isDragging ? 'none' : 'box-shadow 0.12s var(--ease-out)',
        zIndex: isDragging ? 10 : 1,
      }}
    >
      {swapReady && <div data-testid="kanban-swap-hint" style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '0.68rem', color: '#34D399' }}>⇄ Relâche pour échanger les dates</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {prio && <span style={{ width: 6, height: 6, borderRadius: '50%', background: prio, flexShrink: 0 }} />}
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, lineHeight: 1.3, flex: 1, letterSpacing: '-0.005em' }}>{task.title}</div>
      </div>
      {(task.video_format || task.due_date || task.scheduled_post_date) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: 'var(--t3)' }}>
          {task.video_format && (
            <span style={{ padding: '2px 7px', borderRadius: 999, background: 'rgba(167,139,250,0.14)', border: '1px solid rgba(167,139,250,0.3)', color: '#B294F5', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 9.5, fontWeight: 700 }}>{task.video_format}</span>
          )}
          <PubCheckBadge check={task.pub_check} />
          {task.stage === 'to_post' && task.scheduled_post_date
            ? <span data-testid={`kanban-post-date-${task.id}`} style={{ color: '#F5C56A' }}>· 📱 post le {new Date(`${task.scheduled_post_date.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>
            : task.due_date && task.stage !== 'review' && <span>· {new Date(task.due_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>}
        </div>
      )}
      {task.stage === 'review' && task.delivery && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <DeliveryBadge delivery={task.delivery} id={task.id} />
          <PostDayButton videoId={task.id} delivery={task.delivery} compact onDone={onChanged} />
        </div>
      )}
      {task.stage === 'review' && !task.delivery && <ReviewDateControl task={task} onChanged={onChanged} />}
    </div>
  );
}

function KanbanCardPreview({ task }) {
  const s = STAGES[task.stage || 'todo'] || STAGES.todo;
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 10, maxWidth: 240,
      background: `linear-gradient(180deg, ${s.color}22 0%, ${s.color}14 100%)`,
      border: `1px solid ${s.color}cc`, borderLeft: `3px solid ${s.color}`,
      color: 'var(--t1)',
      fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
      boxShadow: '0 24px 50px -10px rgba(0,0,0,0.6)',
      cursor: 'grabbing',
    }}>{task.title}</div>
  );
}


// ─── Iter 62fo — ProjectCalendarDialog : vue mensuelle avec drag & drop des vidéos ───
// Affiche toutes les vidéos du projet (actives) sur leur jour de post, avec DnD
// pour switch rapidement les jours. Les vidéos "to_post" / "done" sont incluses
// car elles ont une date de post pertinente.
function ProjectCalendarDialog({ project, tasks, onClose, onChanged }) {
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [dragged, setDragged] = useState(null);

  // Vidéos avec due_date, non archivées
  const videos = useMemo(() => tasks.filter((t) => t.kind === 'video' && !t.archived && (t.due_date || t.scheduled_post_date)), [tasks]);

  const byDate = useMemo(() => {
    const map = new Map();
    videos.forEach((v) => {
      const d = (v.scheduled_post_date || v.due_date || '').slice(0, 10);
      if (!d) return;
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(v);
    });
    return map;
  }, [videos]);

  const monthLabel = useMemo(() => {
    const d = new Date(cursor.year, cursor.month, 1);
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }, [cursor]);

  const days = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const last = new Date(cursor.year, cursor.month + 1, 0);
    const startDay = (first.getDay() + 6) % 7; // Monday-based
    const total = last.getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) {
      const iso = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push(iso);
    }
    return cells;
  }, [cursor]);

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    setDragged(null);
    if (!active || !over) return;
    const task = videos.find((v) => v.id === active.id);
    if (!task) return;
    const newDate = String(over.id).replace(/^date-/, '');
    const currentDate = (task.scheduled_post_date || task.due_date || '').slice(0, 10);
    if (newDate === currentDate) return;
    const patch = {};
    if (task.scheduled_post_date) patch.scheduled_post_date = newDate;
    patch.due_date = newDate;
    try {
      await api.patch(`/tasks/${task.id}`, patch);
      toast.success(`Déplacée au ${new Date(newDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div
      data-testid="project-calendar-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 140,
        background: 'rgba(6,12,26,0.78)', backdropFilter: 'blur(12px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 980,
          background: 'linear-gradient(180deg, rgba(20,29,66,0.72) 0%, rgba(14,22,44,0.98) 100%)',
          borderRadius: 22, border: '1px solid var(--bd)',
          padding: 20, boxShadow: '0 50px 100px -20px rgba(0,0,0,0.7)',
          maxHeight: '92vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: 'var(--t1)', margin: 0, flex: 1, minWidth: 180, letterSpacing: '-0.01em' }}>
            Calendrier · {project.name}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 4, borderRadius: 11, background: 'var(--sf)', border: '1px solid var(--bd)' }}>
            <button
              data-testid="cal-prev-month"
              title="Mois précédent"
              onClick={() => setCursor((c) => ({ year: c.month === 0 ? c.year - 1 : c.year, month: c.month === 0 ? 11 : c.month - 1 }))}
              style={{ padding: '6px 9px', borderRadius: 8, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.35)', color: 'var(--t1)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
            ><ChevronLeft size={14} /></button>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 13, color: 'var(--t1)', textTransform: 'capitalize', minWidth: 130, textAlign: 'center', letterSpacing: '-0.01em' }}>
              {monthLabel}
            </div>
            <button
              data-testid="cal-next-month"
              title="Mois suivant"
              onClick={() => setCursor((c) => ({ year: c.month === 11 ? c.year + 1 : c.year, month: c.month === 11 ? 0 : c.month + 1 }))}
              style={{ padding: '6px 9px', borderRadius: 8, background: 'rgba(62,123,250,0.14)', border: '1px solid rgba(62,123,250,0.35)', color: 'var(--t1)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
            ><ChevronRight size={14} /></button>
          </div>
          <button
            data-testid="cal-today"
            onClick={() => { const n = new Date(); setCursor({ year: n.getFullYear(), month: n.getMonth() }); }}
            style={{ padding: '7px 12px', borderRadius: 9, background: 'var(--sf)', border: '1px solid var(--bd)', color: 'var(--t2)', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}
          >Aujourd'hui</button>
          <button type="button" onClick={onClose} style={{ padding: '7px 10px', borderRadius: 9, background: 'var(--sf)', border: '1px solid var(--bd)', color: 'var(--t1)', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
        <DndContext sensors={sensors} onDragStart={(e) => setDragged(videos.find((v) => v.id === e.active.id))} onDragEnd={handleDragEnd}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
              <div key={d} style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center', padding: 4 }}>{d}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {days.map((iso, i) => iso ? (
              <CalendarDayCell
                key={iso}
                iso={iso}
                videos={byDate.get(iso) || []}
                isToday={iso === todayIso}
              />
            ) : <div key={`e${i}`} style={{ minHeight: 94 }} />)}
          </div>
          <DragOverlay>
            {dragged && (
              <div style={{
                padding: '5px 9px', borderRadius: 7,
                background: `${STAGES[dragged.stage || 'todo']?.color || '#5BBEFC'}2A`,
                border: `1px solid ${STAGES[dragged.stage || 'todo']?.color || '#5BBEFC'}`,
                color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                boxShadow: '0 20px 40px -8px rgba(0,0,0,0.6)',
              }}>{dragged.title}</div>
            )}
          </DragOverlay>
        </DndContext>
        <div style={{ marginTop: 14, fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: 'var(--t4)', textAlign: 'center' }}>
          Drag une vidéo d'un jour à l'autre pour changer sa date de post. {videos.length} vidéo{videos.length > 1 ? 's' : ''} planifiée{videos.length > 1 ? 's' : ''}.
        </div>
      </div>
    </div>
  );
}

function CalendarDayCell({ iso, videos, isToday }) {
  const { setNodeRef, isOver } = useDroppable({ id: `date-${iso}` });
  const day = parseInt(iso.slice(8), 10);
  return (
    <div
      ref={setNodeRef}
      data-testid={`cal-cell-${iso}`}
      style={{
        minHeight: 110, padding: 6, borderRadius: 10,
        background: isOver ? 'rgba(62,123,250,0.14)' : 'var(--sf)',
        border: `1px solid ${isToday ? '#3E7BFA' : (isOver ? '#3E7BFA' : 'var(--bd)')}`,
        display: 'flex', flexDirection: 'column', gap: 5,
        transition: 'all 0.12s var(--ease-out)',
      }}
    >
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: isToday ? '#5BBEFC' : 'var(--t2)' }}>{day}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignContent: 'flex-start' }}>
        {videos.map((v) => <CalendarVideoChip key={v.id} task={v} />)}
      </div>
    </div>
  );
}

function CalendarVideoChip({ task }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const color = STAGES[task.stage || 'todo']?.color || '#5BBEFC';
  const initials = (task.title || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`cal-chip-${task.id}`}
      title={task.title}
      style={{
        width: 30, height: 30, borderRadius: 7,
        background: `linear-gradient(145deg, ${color}3A 0%, ${color}1A 100%)`,
        border: `1px solid ${color}60`,
        boxShadow: `inset 0 0 0 1px ${color}22`,
        color: 'var(--t1)', cursor: isDragging ? 'grabbing' : 'grab',
        fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
        letterSpacing: '-0.01em',
        display: 'grid', placeItems: 'center',
        flexShrink: 0,
        opacity: isDragging ? 0.3 : 1,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: isDragging ? 'none' : 'all 0.12s var(--ease-out)',
      }}
    >{initials}</div>
  );
}

